import { Component, Input, OnInit, inject, Output, EventEmitter, OnChanges, SimpleChanges, HostListener, OnDestroy } from '@angular/core';
import {
  Receipt,
  CampaignsReceiptAdminApiClient,
  ReceiptHistoryChange,
  UpdateReceiptRequest,
  LineItem,
  CampaignsCampaignAdminApiClient,
  ReceiptDetail
} from 'src/app/api/api-campaigns.service.generated';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTableModule } from '@angular/material/table';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ColumnData, NgxDataTableComponent, PhotoComponent, ConfirmDialogModel } from 'wecheer-common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { ApplyChangesDialogComponent, ApplyChangesDialogData, ApplyChangesDialogResult } from '../../apply-changes-dialog/apply-changes-dialog.component';
import { ChangelogHistoryDialogComponent } from '../../changelog-history-dialog/changelog-history-dialog.component';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { RECEIPT_TAGS } from 'src/app/const/receipt-tag';
import { PERMISSION } from 'src/app/const/permission';
import { ReceiptSavingConfirmationComponent } from '../../receipt-saving-confirmation/receipt-saving-confirmation.component';
import { environment } from 'src/environments/environment';
import { format } from 'date-fns';
import { lastValueFrom } from 'rxjs';
import { HasAnyPermissionDirective } from 'src/app/common/directives/has-any-permission/has-any-permission.directive';
import { FormArray, FormGroup, FormControl, ReactiveFormsModule, Validators, FormsModule } from '@angular/forms';
import { ReceiptPanelComponent } from '../receipt-panel.component';

@UntilDestroy()
@Component({
  selector: 'app-receipt-panel-details',
  templateUrl: './receipt-panel-details.component.html',
  styleUrls: ['./receipt-panel-details.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatCardModule,
    MatChipsModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    MatTabsModule,
    MatTableModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatTooltipModule,
    PhotoComponent,
    NgxDataTableComponent,
    HasAnyPermissionDirective
  ]
})
export class ReceiptPanelDetailsComponent implements OnInit, OnChanges, OnDestroy {
  private snackbar = inject(MatSnackBar);
  private receiptApi = inject(CampaignsReceiptAdminApiClient);
  private campaignsClient = inject(CampaignsCampaignAdminApiClient);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private parentPanel = inject(ReceiptPanelComponent, { optional: true, host: true });

  @Input() receipt!: Receipt; // For backwards compatibility
  @Input() docId?: string; // New input for getting detailed info
  @Output() receiptUpdated = new EventEmitter<Receipt>();
  @Output() imageClicked = new EventEmitter<void>();

  // Multiple receipts with campaigns
  receiptDetails: ReceiptDetail[] = [];
  currentReceiptDetail: ReceiptDetail | null = null;

  isPointsInputInvalid: boolean = false;
  isStampsInputInvalid: boolean = false;

  receiptChanges: ReceiptHistoryChange[] = [];
  raw: any = {
    line_items: [],
    date: null,
    invoice_number: "",
    vendor: {
      name: ""
    }
  };

  columns: ColumnData[] = [
    { key: 'date', label: 'Date' },
    { key: 'change', label: 'Change', colWidth: 50 },
    { key: 'field', label: 'Field' },
    { key: 'from', label: 'From' },
    { key: 'to', label: 'To' },
    { key: 'changedBy', label: 'Changed By' }
  ];

  // Form
  mainForm = new FormGroup({
    lineItems: new FormArray([]),
    note: new FormControl('', [Validators.maxLength(5000)]),
    numberOfStamps: new FormControl<number | null>(null),
    invoiceNumber: new FormControl('', [Validators.maxLength(255)]),
    invoiceDate: new FormControl<string | null>(null),
    invoiceTime: new FormControl<string | null>(null)
  });

  displayedColumns: string[] = ['description', 'quantity', 'price', 'discount', 'total', 'action'];

  // Tags
  newTag: string = '';
  tags: string[] = [];
  isTagEditMode: boolean = false;

  // Tag suggestions for autocomplete
  tagSuggestions: string[] = [
    RECEIPT_TAGS.REPROCESS_MANUAL_REJECT_TAG,
    RECEIPT_TAGS.REPROCESS_CHECK_FRAUD,
    RECEIPT_TAGS.REPROCESS_MANUAL_ACCEPT_TAG,
    RECEIPT_TAGS.REPROCESS_TAG,
    RECEIPT_TAGS.NOT_RECOGNIZABLE_TAG,
    RECEIPT_TAGS.STAMPS_RECEIVED_TAG,
    RECEIPT_TAGS.POINTS_RECEIVED_TAG,
    RECEIPT_TAGS.VERIFY_DUPLICATE_TAG,
    RECEIPT_TAGS.SUSPECT_DUPLICATE_TAG,
    RECEIPT_TAGS.SUSPECT_LOCATION_TAG,
    RECEIPT_TAGS.TIME_CONSTRAINT_NOT_VALID,
    RECEIPT_TAGS.CURRENCY_CONSTRAINT_NOT_PASS,
    RECEIPT_TAGS.NOT_ELIGIBLE_TAG,
    RECEIPT_TAGS.INCOMPLETE_TAG,
    RECEIPT_TAGS.NOT_ENOUGH_STAMPS,
    RECEIPT_TAGS.MANUAL_VERIFY_TAG
  ];
  filteredTagSuggestions: string[] = [];

  // Campaign Information
  campaignInfo: any = null;
  selectedCampaignTab: string = '';
  pointsReceived: number = 0;
  stampsReceived: number = 0;
  originalPointsReceived: number = 0;
  originalStampsReceived: number = 0;
  // Track stamps/points already earned from UserActivity
  pointsAlreadyEarned: number = 0;
  stampsAlreadyEarned: number = 0;
  invoiceNumber: string = '';
  invoiceDateInput: string | null = null;
  invoiceDateIso: string | null = null;
  invoiceTimeInput: string | null = null;

  // Editing states for invoice fields
  isEditingInvoiceNumber: boolean = false;
  isEditingInvoiceDate: boolean = false;
  isEditingInvoiceTime: boolean = false;

  // Editing states for points/stamps fields
  isEditingPoints: boolean = false;
  isEditingStamps: boolean = false;

  // Undo timers for points/stamps
  pointsUndoTimer: any = null;
  pointsUndoCountdown: number = 0;
  stampsUndoTimer: any = null;
  stampsUndoCountdown: number = 0;

  // Track pending saves
  hasPendingPointsSave: boolean = false;
  hasPendingStampsSave: boolean = false;
  hasPendingActionSave: boolean = false;
  hasPendingLineItemsSave: boolean = false;

  // Undo timer for line items
  lineItemsUndoTimer: any = null;
  lineItemsUndoCountdown: number = 0;
  originalLineItemsForUndo: any[] = [];

  // Editing states for admin notes
  isEditingNote: boolean = false;
  originalNote: string = '';

  // Undo timer for action buttons (single undo button for all actions)
  actionUndoTimer: any = null;
  actionUndoCountdown: number = 0;
  showActionUndoButton: boolean = false;
  originalTags: string[] = [];
  currentActionType: 'eligible' | 'fraud' | 'reject' | null = null;

  // Store original state for multiple campaigns undo
  multiCampaignOriginalState: Map<string, {
    tags: string[];
    note: string;
    lineItems: any[];
    numberOfStamps: number | null;
  }> = new Map();
  selectedCampaignIdsForUndo: string[] = [];

    // Open campaign detail in new tab
    openCampaignDetail() {
      if (!this.campaignInfo) return;
      let url;
      const campaign = this.campaignInfo;
      const isMultiCampaign = !campaign.isPunchCardStrategy &&
        !campaign.isLuckyDrawStrategy &&
        !campaign.isStaffCampaignStrategy &&
        !campaign.isReferralCampaignStrategy;
      if (campaign.isStaffCampaignStrategy)
        url = `${environment.baseUrl}/staff-campaign-edit/${campaign.advertiserId}/${campaign.campaignId}`;
      if (campaign.isReferralCampaignStrategy)
        url = `${environment.baseUrl}/referral-campaign-edit/${campaign.advertiserId}/${campaign.campaignId}`;
      if (campaign.isPunchCardStrategy)
        url = `${environment.baseUrl}/punch-card-campaign-edit/${campaign.advertiserId}/${campaign.campaignId}`;
      if (campaign.isLuckyDrawStrategy)
        url = `${environment.baseUrl}/lucky-draw-campaign-edit/${campaign.advertiserId}/${campaign.campaignId}`;
      if (isMultiCampaign)
        url = `${environment.baseUrl}/multi-campaign-edit/${campaign.advertiserId}/${campaign.campaignId}`;
      if (url) {
        window.open(url, '_blank');
      }
    }

    // Open receipt detail in new tab
    openReceiptDetail() {
    if (!this.receipt?.id) return;
    const url = `${environment.baseUrl}/receipts/${this.receipt.id}`;
    window.open(url, '_blank');
    }

  // Track all changes per receipt (each tab = different receipt with unique receiptId)
  // Map key is receiptId (NOT combined with campaignId since each receipt is unique)
  receiptDataMap: Map<string, {
    receiptId: string;
    campaignId: string;
    tags: string[];
    note: string;
    lineItems: any[];
    pointsReceived: number;
    stampsReceived: number;
    originalPointsReceived: number;
    originalStampsReceived: number;
    numberOfStamps: number | null;
    originalTags: string[];
    originalNote: string;
    originalLineItems: any[];
    originalNumberOfStamps: number | null;
    invoiceNumber: string;
    invoiceDateIso: string | null;
    originalInvoiceNumber: string;
    originalInvoiceDateIso: string | null;
    hasTagChanges: boolean;
    hasDetailChanges: boolean;
    hasChanges: boolean;
  }> = new Map();

  // Action buttons state
  isGiveStampsChecked: boolean = false;
  isCheckEligibleChecked: boolean = false;
  isCheckFraudChecked: boolean = false;
  isRejectChecked: boolean = false;
  rejectDisabled: boolean = false;
  giveStampDisabled: boolean = false;
  checkFraudDisabled: boolean = false;
  checkEligibleDisabled: boolean = false;

  saveDisabled: boolean = true;
  isTagChanged = false;
  isDetailsChanged = false;
  saveText = 'Save';
  readonly permissions = PERMISSION;

  selectedTabIndex = 0;
  previousTabIndex = 0;
  tabAnimationDirection: 'left' | 'right' | null = null;

  // Loading states
  isLoadingDetails = false;
  isLoadingHistory = false;
  isLoadingCampaign = false;
  isLoadingOriginalReceipt = false;

  private readonly duplicateTagValues = new Set<string>([
    'duplicate',
    'dublicate',
    RECEIPT_TAGS.VERIFY_DUPLICATE_TAG.toLowerCase(),
    RECEIPT_TAGS.SUSPECT_DUPLICATE_TAG.toLowerCase()
  ]);

  // Session storage key for tracking viewed original receipts
  private readonly VIEWED_ORIGINALS_KEY = 'wecheer_viewed_original_receipts';
  private readonly VIEWED_INVOICE_NUMBERS_KEY = 'wecheer_viewed_invoice_numbers';
  private readonly CURRENT_INVOICE_NUMBER_KEY = 'wecheer_current_invoice_number';

  get lineItemsArray(): FormArray {
    return this.mainForm.get('lineItems') as FormArray;
  }

  /**
   * Get the set of receipt IDs that have been used to view original receipts in this session
   */
  private getViewedOriginalReceiptIds(): Set<string> {
    try {
      const stored = sessionStorage.getItem(this.VIEWED_ORIGINALS_KEY);
      if (stored) {
        return new Set(JSON.parse(stored));
      }
    } catch (e) {
      console.error('Error reading viewed originals from sessionStorage:', e);
    }
    return new Set();
  }

  /**
   * Get the set of invoice numbers that have had their originals viewed
   */
  private getViewedInvoiceNumbers(): Set<string> {
    try {
      const stored = sessionStorage.getItem(this.VIEWED_INVOICE_NUMBERS_KEY);
      if (stored) {
        return new Set(JSON.parse(stored));
      }
    } catch (e) {
      console.error('Error reading viewed invoice numbers from sessionStorage:', e);
    }
    return new Set();
  }

  /**
   * Mark a receipt ID and invoice number as having been used to view its original receipt
   */
  private markOriginalAsViewed(receiptId: string, invoiceNumber: string): void {
    try {
      // Track receipt ID
      const viewedReceipts = this.getViewedOriginalReceiptIds();
      viewedReceipts.add(receiptId);
      sessionStorage.setItem(this.VIEWED_ORIGINALS_KEY, JSON.stringify(Array.from(viewedReceipts)));

      // Track invoice number
      const viewedInvoices = this.getViewedInvoiceNumbers();
      const normalized = invoiceNumber.trim().toLowerCase();
      if (normalized) {
        viewedInvoices.add(normalized);
        sessionStorage.setItem(this.VIEWED_INVOICE_NUMBERS_KEY, JSON.stringify(Array.from(viewedInvoices)));
      }
    } catch (e) {
      console.error('Error saving viewed originals to sessionStorage:', e);
    }
  }

  /**
   * Check if the current receipt ID has been used to view an original receipt
   */
  private hasViewedOriginalForCurrentReceipt(): boolean {
    const receiptId = this.receipt?.id;
    if (!receiptId) {
      return false;
    }
    return this.getViewedOriginalReceiptIds().has(receiptId);
  }

  /**
   * Check if the current receipt's invoice number has had its original viewed
   */
  private hasViewedOriginalForInvoiceNumber(): boolean {
    const invoiceNumber = (this.mainForm.get('invoiceNumber')?.value || this.invoiceNumber || '').trim().toLowerCase();
    if (!invoiceNumber) {
      return false;
    }
    return this.getViewedInvoiceNumbers().has(invoiceNumber);
  }

  /**
   * Clear all viewed originals tracking data
   */
  private clearViewedOriginalsTracking(): void {
    try {
      sessionStorage.removeItem(this.VIEWED_ORIGINALS_KEY);
      sessionStorage.removeItem(this.VIEWED_INVOICE_NUMBERS_KEY);
      sessionStorage.removeItem(this.CURRENT_INVOICE_NUMBER_KEY);
    } catch (e) {
      console.error('Error clearing viewed originals from sessionStorage:', e);
    }
  }

  ngOnInit() {
    // Load immediate data synchronously (fast)
    this.loadImmediateData();

    // Load additional data asynchronously (background)
    this.loadAdditionalData();

    this.mainForm.valueChanges.subscribe(() => {
      this.detailsChangedHandler();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    // This is called every time the @Input() receipt or docId changes
    if ((changes['receipt'] && this.receipt) || (changes['docId'] && this.docId)) {
      // Save any pending changes immediately before switching
      this.executePendingSaves();

      // Load immediate data synchronously (fast)
      this.loadImmediateData();

      // Load additional data asynchronously (background)
      this.loadAdditionalData();
    }
  }

  /**
   * Load immediate data synchronously from the @Input receipt
   * This makes the component display instantly with available data
   */
  private loadImmediateData() {
    // Reset states
    this.isTagChanged = false;
    this.isDetailsChanged = false;
    this.saveText = 'Save';
    this.saveDisabled = true;

    // If no receipt data yet, just return
    if (!this.receipt) return;

    // Check if we're switching to a different invoice number chain
    const currentInvoiceNumber = (this.receipt.invoiceNumber || '').trim().toLowerCase();
    try {
      const storedInvoiceNumber = sessionStorage.getItem(this.CURRENT_INVOICE_NUMBER_KEY);
      if (storedInvoiceNumber && storedInvoiceNumber !== currentInvoiceNumber) {
        // We're switching to a different invoice number, clear all tracking
        this.clearViewedOriginalsTracking();
      }
      // Store the current invoice number
      if (currentInvoiceNumber) {
        sessionStorage.setItem(this.CURRENT_INVOICE_NUMBER_KEY, currentInvoiceNumber);
      }
    } catch (e) {
      console.error('Error managing invoice number tracking:', e);
    }

    // Parse raw JSON immediately
    if (this.receipt?.rawJson) {
      this.raw = JSON.parse(this.receipt.rawJson);
    } else {
      this.raw = {
        line_items: [],
        date: null,
        invoice_number: "",
        vendor: {
          name: ""
        }
      };
    }

    // Load line items from receipt.lineItems if available, otherwise from raw
    const lineItemsToLoad = this.receipt?.lineItems?.length > 0
      ? this.receipt.lineItems.map((item: any) => ({
          id: item.id,
          category: item.type || '',
          description: item.name || '',
          quantity: item.amount ?? 0,
          price: item.price ?? 0,
          discount: item.discount ?? 0,
          total: item.total ?? 0
        }))
      : this.raw?.line_items || [];

    this.patchFormValues(lineItemsToLoad);

    // Load tags from the receipt immediately
    this.tags = this.receipt?.tags ? [...this.receipt.tags] : [];

    // Load invoice metadata
    this.invoiceNumber = this.receipt?.invoiceNumber || '';
    const invoiceDate = this.receipt?.invoiceDate ? new Date(this.receipt.invoiceDate) : null;
    this.invoiceDateIso = invoiceDate ? invoiceDate.toISOString() : null;
    this.invoiceDateInput = invoiceDate ? this.toDateInputString(invoiceDate) : null;
    this.invoiceTimeInput = invoiceDate ? this.toTimeInputString(invoiceDate) : null;

    // Load note and invoice fields immediately
    const tracked = this.receiptDataMap.get(this.receipt?.id || '')?.numberOfStamps;
    this.mainForm.patchValue({
      note: this.receipt?.note || '',
      numberOfStamps: tracked ?? null,
      invoiceNumber: this.invoiceNumber,
      invoiceDate: this.invoiceDateInput,
      invoiceTime: this.invoiceTimeInput
    }, { emitEvent: false });

    // Extract points/stamps from tags immediately
    this.extractPointsStampsFromTags();

    // Update button states based on tags
    this.updateTagChecks(true);
    this.refreshPushToRepocessingStatus(this.receipt?.tags);
  }

  /**
   * Load additional data asynchronously in the background
   * This includes detailed info, history, and campaign data
   */
  private async loadAdditionalData() {
    // If docId is provided, fetch detailed information for all campaigns
    if (this.docId) {
      this.isLoadingDetails = true;
      try {
        const response = await lastValueFrom(this.receiptApi.getReceiptDetailedInfo(this.docId));
        this.receiptDetails = response.receiptDetails || [];

        // CRITICAL FIX: Create deep copies of tags array for each receipt
        this.receiptDetails.forEach(detail => {
          if (detail.receipt?.tags) {
            detail.receipt.tags = [...detail.receipt.tags];
          }
          if (detail.receipt?.lineItems) {
            detail.receipt.lineItems = [...detail.receipt.lineItems];
          }
        });

        // Select the first receipt detail by default
        if (this.receiptDetails.length > 0) {
          this.currentReceiptDetail = this.receiptDetails[0];
          this.receipt = this.currentReceiptDetail.receipt!;
          this.selectedCampaignTab = this.currentReceiptDetail.campaign?.campaignId || '';
          this.receiptChanges = this.currentReceiptDetail.historyChanges || [];
          this.campaignInfo = this.currentReceiptDetail.campaign;

          // Reload immediate data with the detailed receipt
          this.loadImmediateData();

          // Initialize map for receipts
          const firstReceiptId = this.receipt?.id || '';
          if (!this.receiptDataMap.has(firstReceiptId)) {
            this.updateReceiptData(firstReceiptId, {
              tags: this.receipt?.tags ? [...this.receipt.tags] : [],
              note: this.receipt?.note || '',
              lineItems: this.receipt?.lineItems || [],
              pointsReceived: this.pointsReceived,
              stampsReceived: this.stampsReceived,
              originalPointsReceived: this.originalPointsReceived,
              originalStampsReceived: this.originalStampsReceived,
              invoiceNumber: this.invoiceNumber,
              originalInvoiceNumber: this.invoiceNumber,
              invoiceDateIso: this.invoiceDateIso,
              originalInvoiceDateIso: this.invoiceDateIso
            });
          }
        }

        this.isLoadingDetails = false;
      } catch (error) {
        console.error('Error loading receipt detailed info:', error);
  this.snackbar.open('Failed to load receipt details', null, { duration: 3000, horizontalPosition: 'left' });
        this.isLoadingDetails = false;
        return;
      }
    }

    // Load receipt history (only if not using docId mode)
    if (!this.docId) {
      this.isLoadingHistory = true;
      this.receiptChanges = await this.getReceiptChanges();
      this.isLoadingHistory = false;
    }

    // Load campaign information (only if not using docId mode)
    if (!this.docId) {
      this.isLoadingCampaign = true;
      await this.loadCampaignInfo();
      this.isLoadingCampaign = false;
    }
  }

  async loadReceiptData() {
    // Reset states
    this.isTagChanged = false;
    this.isDetailsChanged = false;
    this.saveText = 'Save';
    this.saveDisabled = true;

    // If docId is provided, use GetReceiptDetailedInfo API to get multiple receipts
    if (this.docId) {
      try {
        const response = await lastValueFrom(this.receiptApi.getReceiptDetailedInfo(this.docId));
        this.receiptDetails = response.receiptDetails || [];

        // CRITICAL FIX: Create deep copies of tags array for each receipt
        // The API might return the same receipt object for all campaigns with shared array references
        // This ensures each campaign has its own independent tags array
        this.receiptDetails.forEach(detail => {
          if (detail.receipt?.tags) {
            detail.receipt.tags = [...detail.receipt.tags];
          }
          if (detail.receipt?.lineItems) {
            detail.receipt.lineItems = [...detail.receipt.lineItems];
          }
        });

        // Select the first receipt detail by default
        if (this.receiptDetails.length > 0) {
          this.currentReceiptDetail = this.receiptDetails[0];
          this.receipt = this.currentReceiptDetail.receipt!;
          this.selectedCampaignTab = this.currentReceiptDetail.campaign?.campaignId || '';
          this.receiptChanges = this.currentReceiptDetail.historyChanges || [];
          this.campaignInfo = this.currentReceiptDetail.campaign;
        }
      } catch (error) {
        console.error('Error loading receipt detailed info:', error);
  this.snackbar.open('Failed to load receipt details', null, { duration: 3000, horizontalPosition: 'left' });
        return;
      }
    }

    // Parse raw JSON
    if (this.receipt?.rawJson) {
      this.raw = JSON.parse(this.receipt.rawJson);
    } else {
      this.raw = {
        line_items: [],
        date: null,
        invoice_number: "",
        vendor: {
          name: ""
        }
      };
    }

    // Load line items from receipt.lineItems if available, otherwise from raw
    const lineItemsToLoad = this.receipt?.lineItems?.length > 0
      ? this.receipt.lineItems.map((item: any) => ({
          id: item.id,
          category: item.type || '',
          description: item.name || '',
          quantity: item.amount ?? 0,
          price: item.price ?? 0,
          discount: item.discount ?? 0,
          total: item.total ?? 0
        }))
      : this.raw?.line_items || [];

    this.patchFormValues(lineItemsToLoad);

    this.invoiceNumber = this.receipt?.invoiceNumber || '';
    const invoiceDate = this.receipt?.invoiceDate ? new Date(this.receipt.invoiceDate) : null;
    this.invoiceDateIso = invoiceDate ? invoiceDate.toISOString() : null;
    this.invoiceDateInput = invoiceDate ? this.toDateInputString(invoiceDate) : null;

    // Initialize map for the first receipt (if using docId mode with multiple campaigns)
    if (this.receiptDetails.length > 0 && this.campaignInfo?.campaignId) {
      const firstReceiptId = this.receipt?.id || '';

      if (!this.receiptDataMap.has(firstReceiptId)) {
        this.updateReceiptData(firstReceiptId, {
          tags: this.receipt?.tags ? [...this.receipt.tags] : [],
          note: this.receipt?.note || '',
          lineItems: lineItemsToLoad,
          pointsReceived: 0,
          stampsReceived: 0,
          originalPointsReceived: 0,
          originalStampsReceived: 0,
          invoiceNumber: this.invoiceNumber,
          originalInvoiceNumber: this.invoiceNumber,
          invoiceDateIso: this.invoiceDateIso,
          originalInvoiceDateIso: this.invoiceDateIso
        });
      }
    }

    // Load tags from the first receipt
    this.tags = this.receipt?.tags ? [...this.receipt.tags] : [];

    // Load note and number of stamps
    const savedNumber = this.receiptDataMap.get(this.receipt?.id || '')?.numberOfStamps;
    this.mainForm.patchValue({
      note: this.receipt?.note || '',
      numberOfStamps: savedNumber ?? null,
      invoiceNumber: this.invoiceNumber,
      invoiceDate: this.invoiceDateInput
    }, { emitEvent: false });

    // Load receipt history (only if not using docId mode, since we already loaded it)
    if (!this.docId) {
      this.receiptChanges = await this.getReceiptChanges();
    }

    // Load campaign information (only if not using docId mode, since we already loaded it)
    if (!this.docId) {
      await this.loadCampaignInfo();
    }

    // Extract points/stamps from tags
    this.extractPointsStampsFromTags();

    // Update button states based on tags
    this.updateTagChecks(true);
    this.refreshPushToRepocessingStatus(this.receipt?.tags);
  }

  async loadCampaignInfo() {
    if (this.receipt?.campaignId) {
      try {
        this.campaignInfo = await lastValueFrom(this.campaignsClient.getCampaign(this.receipt.campaignId));
        this.selectedCampaignTab = this.campaignInfo?.campaignId || '';

        // Extract points/stamps from tags
        this.extractPointsStampsFromTags();
      } catch (error) {
        console.error('Error loading campaign info:', error);
        this.campaignInfo = null;
      }
    } else {
      this.campaignInfo = null;
    }
  }

  private toDateInputString(date: Date): string {
    try {
      // Extract UTC date components to avoid timezone conversion
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch {
      return '';
    }
  }

  private toTimeInputString(date: Date): string {
    try {
      // Extract UTC time components to avoid timezone conversion
      const hours = String(date.getUTCHours()).padStart(2, '0');
      const minutes = String(date.getUTCMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    } catch {
      return '';
    }
  }

  private convertDateInputToIso(dateInput: string | null | undefined, timeInput?: string | null): string | null {
    if (!dateInput) {
      return null;
    }
    const time = timeInput || '00:00';
    const parsed = new Date(`${dateInput}T${time}:00Z`);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }
    return parsed.toISOString();
  }

  private convertIsoToDateInput(iso: string | null | undefined): string | null {
    if (!iso) {
      return null;
    }
    const parsed = new Date(iso);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }
    const inputValue = this.toDateInputString(parsed);
    return inputValue || null;
  }

  onInvoiceNumberFocus(): void {
    this.isEditingInvoiceNumber = true;
  }

  onInvoiceNumberEnter(event: Event): void {
    event.preventDefault();
    this.submitInvoiceNumber();
  }

  async submitInvoiceNumber(): Promise<void> {
    this.commitInvoiceNumber();
    await this.saveInvoiceFieldsImmediately();
    this.isEditingInvoiceNumber = false;
  }

  cancelInvoiceNumber(): void {
    const control = this.mainForm.get('invoiceNumber');
    if (control) {
      control.setValue(this.invoiceNumber, { emitEvent: false });
    }
    this.isEditingInvoiceNumber = false;
  }

  onInvoiceDateFocus(): void {
    this.isEditingInvoiceDate = true;
  }

  async submitInvoiceDate(): Promise<void> {
    this.commitInvoiceDate();
    await this.saveInvoiceFieldsImmediately();
    this.isEditingInvoiceDate = false;
  }

  cancelInvoiceDate(): void {
    const control = this.mainForm.get('invoiceDate');
    if (control) {
      control.setValue(this.invoiceDateInput, { emitEvent: false });
    }
    this.isEditingInvoiceDate = false;
  }

  onInvoiceTimeFocus(): void {
    this.isEditingInvoiceTime = true;
  }

  async submitInvoiceTime(): Promise<void> {
    this.commitInvoiceDate();
    await this.saveInvoiceFieldsImmediately();
    this.isEditingInvoiceTime = false;
  }

  cancelInvoiceTime(): void {
    const control = this.mainForm.get('invoiceTime');
    if (control) {
      control.setValue(this.invoiceTimeInput, { emitEvent: false });
    }
    this.isEditingInvoiceTime = false;
  }

  private commitInvoiceNumber(): void {
    const control = this.mainForm.get('invoiceNumber');
    if (!control) {
      return;
    }

    const rawValue = (control.value ?? '') as string;
    const normalizedValue = rawValue.trim();

    if (normalizedValue !== rawValue) {
      control.setValue(normalizedValue, { emitEvent: false });
    }

    const currentDateValue = (this.mainForm.get('invoiceDate')?.value ?? this.invoiceDateInput) as string | null;

    this.applyInvoiceChanges(normalizedValue, currentDateValue && currentDateValue.length ? currentDateValue : null);
  }

  private commitInvoiceDate(): void {
    const dateControl = this.mainForm.get('invoiceDate');
    const timeControl = this.mainForm.get('invoiceTime');

    if (!dateControl) {
      return;
    }

    const rawDateValue = dateControl.value as string | null;
    const rawTimeValue = timeControl?.value as string | null;
    const normalizedDateValue = rawDateValue && rawDateValue.length ? rawDateValue : null;
    const normalizedTimeValue = rawTimeValue && rawTimeValue.length ? rawTimeValue : null;

    const numberValueRaw = this.mainForm.get('invoiceNumber')?.value ?? this.invoiceNumber;
    const numberValue = typeof numberValueRaw === 'string' ? numberValueRaw : this.invoiceNumber;

    this.applyInvoiceChanges(numberValue, normalizedDateValue, normalizedTimeValue);
  }

  private applyInvoiceChanges(invoiceNumberValue: string | null | undefined, invoiceDateInput: string | null, invoiceTimeInput?: string | null): void {
    const normalizedNumber = (invoiceNumberValue ?? '').trim();
    const normalizedDateInput = invoiceDateInput && invoiceDateInput.length ? invoiceDateInput : null;
    const normalizedTimeInput = invoiceTimeInput && invoiceTimeInput.length ? invoiceTimeInput : null;
    const normalizedIso = this.convertDateInputToIso(normalizedDateInput, normalizedTimeInput);

    const hasNumberChanged = normalizedNumber !== this.invoiceNumber;
    const hasDateChanged = normalizedIso !== this.invoiceDateIso;

    if (!hasNumberChanged && !hasDateChanged) {
      if (this.mainForm.get('invoiceNumber')?.value !== normalizedNumber ||
          this.mainForm.get('invoiceDate')?.value !== normalizedDateInput ||
          this.mainForm.get('invoiceTime')?.value !== normalizedTimeInput) {
        this.mainForm.patchValue({
          invoiceNumber: normalizedNumber,
          invoiceDate: normalizedDateInput,
          invoiceTime: normalizedTimeInput
        }, { emitEvent: false });
      }
      return;
    }

    this.invoiceNumber = normalizedNumber;
    this.invoiceDateInput = normalizedDateInput;
    this.invoiceTimeInput = normalizedTimeInput;
    this.invoiceDateIso = normalizedIso;

    this.mainForm.patchValue({
      invoiceNumber: normalizedNumber,
      invoiceDate: normalizedDateInput,
      invoiceTime: normalizedTimeInput
    }, { emitEvent: false });

    this.syncInvoiceFieldsAcrossReceipts(normalizedNumber, normalizedIso);

    this.saveCampaignChanges();
    this.isDetailsChanged = true;
    this.refreshPushToRepocessingStatus(this.tags);
  }

  private syncInvoiceFieldsAcrossReceipts(invoiceNumber: string, invoiceDateIso: string | null): void {
    const normalizedNumber = invoiceNumber.trim();

  this.receipt.invoiceNumber = normalizedNumber || undefined;
  this.receipt.invoiceDate = invoiceDateIso ? new Date(invoiceDateIso) : undefined;

    this.raw.invoice_number = normalizedNumber;
    this.raw.date = invoiceDateIso;

    if (this.receipt.rawJson) {
      try {
        const rawPayload = JSON.parse(this.receipt.rawJson);
        rawPayload.invoice_number = normalizedNumber;
        rawPayload.date = invoiceDateIso;
        this.receipt.rawJson = JSON.stringify(rawPayload);
        this.raw = rawPayload;
      } catch (error) {
        console.error('Failed to update local raw JSON for invoice fields:', error);
      }
    }

    this.receiptDetails.forEach(detail => {
      if (!detail.receipt) {
        return;
      }

  detail.receipt.invoiceNumber = normalizedNumber || undefined;
  detail.receipt.invoiceDate = invoiceDateIso ? new Date(invoiceDateIso) : undefined;

      if (detail.receipt.rawJson) {
        try {
          const rawDetail = JSON.parse(detail.receipt.rawJson);
          rawDetail.invoice_number = normalizedNumber;
          rawDetail.date = invoiceDateIso;
          detail.receipt.rawJson = JSON.stringify(rawDetail);
        } catch (error) {
          console.error('Failed to update detail raw JSON for invoice fields:', detail.receipt.id, error);
        }
      }
    });
  }

  private async saveInvoiceFieldsImmediately(): Promise<void> {
    if (!this.receipt?.id) {
      console.error('Cannot save invoice fields: receipt ID is missing');
      return;
    }

    try {
      // Prepare the receipt with updated invoice fields
      const { lineItemData } = this.mapLineItemsForSave(this.receipt.lineItems);

      const updatedReceipt = new Receipt({
        ...this.receipt,
        invoiceNumber: this.invoiceNumber || undefined,
        invoiceDate: this.invoiceDateIso ? new Date(this.invoiceDateIso) : undefined,
        lineItems: lineItemData
      });

        const request = new UpdateReceiptRequest({
          actions: this.receipt.tags || [],
          note: this.receipt.note || '',
          numberOfStamps: this.mainForm.get('numberOfStamps')?.value ?? null,
          receipt: updatedReceipt,
          isReprocess: false,
          isUpdateReceipt: true
        });

      await lastValueFrom(this.receiptApi.updateReceiptById(this.receipt.id, request));

  this.snackbar.open('Invoice fields updated successfully', null, { duration: 3000, horizontalPosition: 'left' });

      // Update original values so we don't mark as changed anymore
      const receiptId = this.receipt.id;
      const savedData = this.receiptDataMap.get(receiptId);
      if (savedData) {
        savedData.originalInvoiceNumber = this.invoiceNumber;
        savedData.originalInvoiceDateIso = this.invoiceDateIso;
        this.receiptDataMap.set(receiptId, savedData);
      }
    } catch (error) {
      console.error('Failed to save invoice fields:', error);
  this.snackbar.open('Failed to update invoice fields', null, { duration: 3000, horizontalPosition: 'left' });
    }
  }

  extractPointsStampsFromTags() {
    const receiptId = this.receipt?.id || 'default';

    // First, extract already earned stamps/points from UserActivity
    this.extractAlreadyEarnedRewards();

    // Then parse manual rewards from tags
    const manualRewards = this.parseManualRewards(this.tags);

    // If there are no manual rewards in tags, initialize with already earned values
    // Otherwise, add the manual adjustment to the already earned values
    if (manualRewards.stamps === 0 && manualRewards.points === 0) {
      // No manual tags - show what user already earned
      this.stampsReceived = this.stampsAlreadyEarned;
      this.pointsReceived = this.pointsAlreadyEarned;
    } else {
      // Has manual tags - the tag value represents the DIFFERENCE to apply
      // So total = alreadyEarned + manualAdjustment
      this.stampsReceived = this.stampsAlreadyEarned + manualRewards.stamps;
      this.pointsReceived = this.pointsAlreadyEarned + manualRewards.points;
    }

    this.originalStampsReceived = this.stampsReceived;
    this.originalPointsReceived = this.pointsReceived;

    // Update receipt data
    this.updateReceiptData(receiptId, {
      pointsReceived: this.pointsReceived,
      stampsReceived: this.stampsReceived,
      originalPointsReceived: this.originalPointsReceived,
      originalStampsReceived: this.originalStampsReceived
    });
  }

  extractAlreadyEarnedRewards() {
    this.pointsAlreadyEarned = 0;
    this.stampsAlreadyEarned = 0;

    // Check if we have current receipt detail with user activity
    if (this.currentReceiptDetail?.userActivity && this.currentReceiptDetail.userActivity.length > 0) {
      for (const activity of this.currentReceiptDetail.userActivity) {
        if (!activity.eventData) {
          continue;
        }

        const eventData = activity.eventData;

        // Existing event where both points and stamps might be present
        if (activity.eventType === 'EventPointsReceived') {
          if (eventData.stamps !== undefined) {
            this.stampsAlreadyEarned += Number(eventData.stamps) || 0;
          } else if (eventData.numberOfStamps !== undefined) {
            this.stampsAlreadyEarned += Number(eventData.numberOfStamps) || 0;
          }

          if (eventData.points !== undefined) {
            this.pointsAlreadyEarned += Number(eventData.points) || 0;
          } else if (eventData.rewardPoints !== undefined) {
            this.pointsAlreadyEarned += Number(eventData.rewardPoints) || 0;
          }
        }

        if (activity.eventType === 'EventPunchcardStampsReceived') {
          if (eventData.givenStamps !== undefined) {
            this.stampsAlreadyEarned += Number(eventData.givenStamps) || 0;
          }
        }
      }
    }
  }

  private getFormLineItems(): any[] {
    const lineItemsArray = this.mainForm.get('lineItems') as FormArray;
    return lineItemsArray.controls.map(control => control.value);
  }

  private updateReceiptData(receiptId: string, updates: Partial<{
    tags: string[];
    note: string;
    lineItems: any[];
    pointsReceived: number;
    stampsReceived: number;
    originalPointsReceived: number;
    originalStampsReceived: number;
    numberOfStamps: number | null;
    originalTags: string[];
    originalNote: string;
    originalLineItems: any[];
    originalNumberOfStamps: number | null;
    invoiceNumber: string;
    invoiceDateIso: string | null;
    originalInvoiceNumber: string;
    originalInvoiceDateIso: string | null;
  }>) {
    const campaignId = this.selectedCampaignTab || this.campaignInfo?.campaignId || 'default';

    const initialTags = updates.originalTags ?? (this.receipt?.tags ? [...this.receipt.tags] : []);
    const initialNote = updates.originalNote ?? (this.receipt?.note || '');
    const initialLineItems = updates.originalLineItems ?? this.cloneLineItems(this.receipt?.lineItems || []);
    const initialNumberOfStamps = updates.originalNumberOfStamps ?? (this.mainForm.get('numberOfStamps')?.value ?? null);
    const currentInvoiceNumber = updates.invoiceNumber ?? (this.mainForm.get('invoiceNumber')?.value ?? this.invoiceNumber ?? this.receipt?.invoiceNumber ?? '');
    const currentInvoiceDateIso = updates.invoiceDateIso ?? (this.invoiceDateIso ?? (this.receipt?.invoiceDate ? new Date(this.receipt.invoiceDate).toISOString() : null));
    const initialInvoiceNumber = updates.originalInvoiceNumber ?? (this.receipt?.invoiceNumber ?? '');
    const initialInvoiceDateIso = updates.originalInvoiceDateIso ?? (this.receipt?.invoiceDate ? new Date(this.receipt.invoiceDate).toISOString() : null);

    const existing = this.receiptDataMap.get(receiptId) || {
      receiptId,
      campaignId,
      tags: updates.tags ? [...updates.tags] : [...this.tags],
      note: updates.note ?? (this.mainForm.get('note')?.value || ''),
      lineItems: updates.lineItems ? this.cloneLineItems(updates.lineItems) : this.cloneLineItems(this.getFormLineItems()),
      pointsReceived: updates.pointsReceived ?? this.pointsReceived,
      stampsReceived: updates.stampsReceived ?? this.stampsReceived,
      originalPointsReceived: updates.originalPointsReceived ?? this.originalPointsReceived,
      originalStampsReceived: updates.originalStampsReceived ?? this.originalStampsReceived,
      numberOfStamps: updates.numberOfStamps ?? (this.mainForm.get('numberOfStamps')?.value ?? null),
      originalTags: [...initialTags],
      originalNote: initialNote,
      originalLineItems: this.cloneLineItems(initialLineItems),
      originalNumberOfStamps: initialNumberOfStamps,
      invoiceNumber: currentInvoiceNumber,
      invoiceDateIso: currentInvoiceDateIso,
      originalInvoiceNumber: initialInvoiceNumber,
      originalInvoiceDateIso: initialInvoiceDateIso,
      hasTagChanges: false,
      hasDetailChanges: false,
      hasChanges: false
    };

    existing.campaignId = campaignId;

    if (updates.tags) {
      existing.tags = [...updates.tags];
    }
    if (updates.note !== undefined) {
      existing.note = updates.note;
    }
    if (updates.lineItems) {
      existing.lineItems = this.cloneLineItems(updates.lineItems);
    }
    if (updates.pointsReceived !== undefined) {
      existing.pointsReceived = updates.pointsReceived;
    }
    if (updates.stampsReceived !== undefined) {
      existing.stampsReceived = updates.stampsReceived;
    }
    if (updates.originalPointsReceived !== undefined) {
      existing.originalPointsReceived = updates.originalPointsReceived;
    }
    if (updates.originalStampsReceived !== undefined) {
      existing.originalStampsReceived = updates.originalStampsReceived;
    }
    if (updates.numberOfStamps !== undefined) {
      existing.numberOfStamps = updates.numberOfStamps;
    }
    if (updates.originalTags) {
      existing.originalTags = [...updates.originalTags];
    }
    if (updates.originalNote !== undefined) {
      existing.originalNote = updates.originalNote;
    }
    if (updates.originalLineItems) {
      existing.originalLineItems = this.cloneLineItems(updates.originalLineItems);
    }
    if (updates.originalNumberOfStamps !== undefined) {
      existing.originalNumberOfStamps = updates.originalNumberOfStamps;
    }
    if (updates.invoiceNumber !== undefined) {
      existing.invoiceNumber = updates.invoiceNumber;
    }
    if (updates.invoiceDateIso !== undefined) {
      existing.invoiceDateIso = updates.invoiceDateIso;
    }
    if (updates.originalInvoiceNumber !== undefined) {
      existing.originalInvoiceNumber = updates.originalInvoiceNumber;
    }
    if (updates.originalInvoiceDateIso !== undefined) {
      existing.originalInvoiceDateIso = updates.originalInvoiceDateIso;
    }

    existing.hasTagChanges = !this.arrayEquals(existing.tags, existing.originalTags);
    const noteChanged = existing.note !== existing.originalNote;
    const lineItemsChanged = !this.lineItemsEqual(existing.lineItems, existing.originalLineItems);
    const numberOfStampsChanged = existing.numberOfStamps !== existing.originalNumberOfStamps;
    const pointsChanged = existing.pointsReceived !== existing.originalPointsReceived;
    const stampsChanged = existing.stampsReceived !== existing.originalStampsReceived;

    const invoiceNumberChanged = (existing.invoiceNumber ?? '') !== (existing.originalInvoiceNumber ?? '');
    const invoiceDateChanged = (existing.invoiceDateIso ?? null) !== (existing.originalInvoiceDateIso ?? null);

    existing.hasDetailChanges = noteChanged || lineItemsChanged || numberOfStampsChanged || pointsChanged || stampsChanged || invoiceNumberChanged || invoiceDateChanged;
    existing.hasChanges = existing.hasTagChanges || existing.hasDetailChanges;

    this.receiptDataMap.set(receiptId, existing);
  }

  private cloneLineItems(source: any[] | undefined): any[] {
    if (!source || source.length === 0) {
      return [];
    }
    try {
      return JSON.parse(JSON.stringify(source));
    } catch {
      return Array.isArray(source) ? [...source] : [];
    }
  }

  private arrayEquals<T>(a: T[] | undefined, b: T[] | undefined): boolean {
    if (a === b) {
      return true;
    }
    if (!a || !b || a.length !== b.length) {
      return false;
    }
    return a.every((value, index) => value === b[index]);
  }

  private lineItemsEqual(a: any[] | undefined, b: any[] | undefined): boolean {
    return JSON.stringify(a ?? []) === JSON.stringify(b ?? []);
  }

  private mapLineItemsForSave(source: any[] | undefined): { lineItemData: LineItem[]; rawLineItems: any[] } {
    if (!source || source.length === 0) {
      return { lineItemData: [], rawLineItems: [] };
    }

    const rawLineItems = source.map(item => {
      const description = item?.description ?? item?.name ?? '';
      const category = item?.category ?? item?.type ?? '';
      const quantity = this.toNumber(item?.quantity ?? item?.amount);
      const price = this.toNumber(item?.price);
      const discount = this.toNumber(item?.discount);
      const total = this.toNumber(item?.total ?? item?.price ?? 0);

      return {
        id: item?.id ?? null,
        category,
        description,
        quantity,
        price,
        discount,
        total
      };
    });

    const lineItemData = rawLineItems.map(lineItem => new LineItem({
      name: lineItem.description,
      amount: lineItem.quantity ?? 0,
      price: lineItem.price ?? 0,
      discount: lineItem.discount ?? 0,
      total: lineItem.total ?? 0
    }));

    return { lineItemData, rawLineItems };
  }

  private toNumber(value: any): number {
    const numeric = Number(value ?? 0);
    return isNaN(numeric) ? 0 : numeric;
  }

  private getNumberOfStampsForPayload(receiptId?: string): number | null {
    const formValue = this.mainForm.get('numberOfStamps')?.value;
    if (formValue !== undefined && formValue !== null) return formValue;
    const id = receiptId ?? (this.receipt?.id || 'default');
    const saved = this.receiptDataMap.get(id);
    if (saved && saved.numberOfStamps !== undefined && saved.numberOfStamps !== null) return saved.numberOfStamps;
    return null;
  }

  private parseManualRewards(tags: string[] | undefined): { points: number; stamps: number } {
    if (!tags || tags.length === 0) {
      return { points: 0, stamps: 0 };
    }

    const manualGiveStampsTag = tags.find(tag => tag.startsWith(RECEIPT_TAGS.REPROCESS_MANUAL_GIVE_STAMPS_PREFIX_TAG));
    const manualGivePointsTag = tags.find(tag => tag.startsWith(RECEIPT_TAGS.REPROCESS_MANUAL_GIVE_POINTS_PREFIX_TAG));

    const parseValue = (tag: string | undefined): number => {
      if (!tag) {
        return 0;
      }
      const parts = tag.split('_');
      if (parts.length > 1) {
        const value = parseInt(parts[1], 10);
        return isNaN(value) ? 0 : value;
      }
      return 0;
    };

    return {
      points: parseValue(manualGivePointsTag),
      stamps: parseValue(manualGiveStampsTag)
    };
  }

  private resolveCampaignActionLabel(savedData: {
    hasTagChanges: boolean;
    hasDetailChanges: boolean;
  } | undefined): string {
    if (!savedData) {
      return '';
    }

    if (savedData.hasTagChanges) {
      return 'reprocess';
    }

    if (savedData.hasDetailChanges) {
      return 'save';
    }

    return '';
  }

  onTabChange(index: number) {
    // Determine animation direction based on tab index change
    if (index > this.previousTabIndex) {
      this.tabAnimationDirection = 'right';
    } else if (index < this.previousTabIndex) {
      this.tabAnimationDirection = 'left';
    } else {
      this.tabAnimationDirection = null;
    }

    const animationDelay = this.tabAnimationDirection ? 220 : 0;
    this.previousTabIndex = index;

    const applyTabChange = () => {
      if (this.receiptDetails.length > 0 && index < this.receiptDetails.length) {
        const selectedDetail = this.receiptDetails[index];
        this.selectCampaignTab(selectedDetail.campaign?.campaignId || '');
      }
    };

    if (animationDelay > 0) {
      setTimeout(() => requestAnimationFrame(applyTabChange), animationDelay);
    } else {
      requestAnimationFrame(applyTabChange);
    }
  }

  selectCampaignTab(campaignId: string) {
    // Execute any pending saves before switching campaigns
    this.executePendingSaves();

    // Save current campaign/receipt data before switching
    if (this.selectedCampaignTab && this.receipt?.id) {
      const receiptId = this.receipt.id;
      this.updateReceiptData(receiptId, {
        tags: [...this.tags],
        note: this.mainForm.get('note')?.value || '',
        lineItems: this.getFormLineItems(),
        pointsReceived: this.pointsReceived,
        stampsReceived: this.stampsReceived,
        originalPointsReceived: this.originalPointsReceived,
        originalStampsReceived: this.originalStampsReceived
      });
    }

    this.selectedCampaignTab = campaignId;

    // If using docId mode with multiple receipts, switch to the selected campaign's receipt
    if (this.receiptDetails.length > 0) {
      const selectedDetail = this.receiptDetails.find(detail => detail.campaign?.campaignId === campaignId);
      if (selectedDetail) {

        this.currentReceiptDetail = selectedDetail;
        this.receipt = selectedDetail.receipt!;
        this.receiptChanges = selectedDetail.historyChanges || [];
        this.campaignInfo = selectedDetail.campaign;

        this.loadReceiptDataForCurrentDetail();
      }
    }
  }

  private loadReceiptDataForCurrentDetail() {
    // Use the optimized immediate data loading for fast tab switching
    this.loadImmediateData();

    // Check if this receipt already has tracked changes, restore from map if available
    const receiptId = this.receipt?.id || '';
    const savedData = this.receiptDataMap.get(receiptId);

    if (savedData) {
      // Restore from saved data
      this.tags = [...savedData.tags];
      this.invoiceNumber = savedData.invoiceNumber ?? '';
      this.invoiceDateIso = savedData.invoiceDateIso ?? null;
      this.invoiceDateInput = this.convertIsoToDateInput(savedData.invoiceDateIso);
      this.invoiceTimeInput = savedData.invoiceDateIso ? this.toTimeInputString(new Date(savedData.invoiceDateIso)) : null;
      this.mainForm.patchValue({
        note: savedData.note,
        numberOfStamps: savedData.numberOfStamps ?? null,
        invoiceNumber: this.invoiceNumber,
        invoiceDate: this.invoiceDateInput,
        invoiceTime: this.invoiceTimeInput
      }, { emitEvent: false });

      // Restore points/stamps
      this.pointsReceived = savedData.pointsReceived;
      this.stampsReceived = savedData.stampsReceived;
      this.originalPointsReceived = savedData.originalPointsReceived;
      this.originalStampsReceived = savedData.originalStampsReceived;
    } else {
      // First time loading this receipt - initialize map
      const originalTags = this.receipt?.tags ? [...this.receipt.tags] : [];
      this.tags = [...originalTags];

      // Initialize map entry with the original receipt data
      this.updateReceiptData(receiptId, {
        tags: [...originalTags],
        note: this.receipt?.note || '',
        lineItems: this.receipt?.lineItems || [],
        pointsReceived: this.pointsReceived,
        stampsReceived: this.stampsReceived,
        originalPointsReceived: this.originalPointsReceived,
        originalStampsReceived: this.originalStampsReceived,
        invoiceNumber: this.invoiceNumber,
        invoiceDateIso: this.invoiceDateIso,
        originalInvoiceNumber: this.invoiceNumber,
        originalInvoiceDateIso: this.invoiceDateIso
      });

      // Initialize tracking for this campaign
      this.saveCampaignChanges();
    }
  }

  // Save current receipt's changes to tracking map
  private saveCampaignChanges(): void {
    const receiptId = this.receipt?.id || '';
    if (!receiptId) return;

    this.updateReceiptData(receiptId, {
      tags: [...this.tags],
      note: this.mainForm.get('note')?.value || '',
      lineItems: this.getFormLineItems(),
      pointsReceived: this.pointsReceived,
      stampsReceived: this.stampsReceived,
      originalPointsReceived: this.originalPointsReceived,
      originalStampsReceived: this.originalStampsReceived,
      numberOfStamps: this.mainForm.get('numberOfStamps')?.value ?? null,
      invoiceNumber: this.invoiceNumber,
      invoiceDateIso: this.invoiceDateIso
    });
  }

  getCampaignType(): string {
    if (!this.campaignInfo) return 'N/A';

    if (this.campaignInfo.isPunchCardStrategy) return 'Punch Card';
    if (this.campaignInfo.isLuckyDrawStrategy) return 'Lucky Draw';
    if (this.campaignInfo.isStaffCampaignStrategy) return 'Staff Campaign';
    if (this.campaignInfo.isStaffPointStrategy) return 'Points Collection';
    if (this.campaignInfo.isReferralCampaignStrategy) return 'Referral Campaign';
    if (this.campaignInfo.isOpenerStrategy) return 'Opener Strategy';
    if (this.campaignInfo.isConsumerInformationStrategy) return 'Consumer Information';
    if (this.campaignInfo.isStaffInformationStrategy) return 'Staff Information';

    return 'Multi Campaign';
  }

  isStampsBasedCampaign(): boolean {
    const campaign = this.currentReceiptDetail?.campaign || this.campaignInfo;
    if (!campaign) return false;

    // Check strategy type first
    if (campaign.strategy?.strategyType === 'Stamps') {
      return true;
    }

    // Fallback: Punch Card campaigns use stamps
    return campaign.isPunchCardStrategy === true;
  }

  isPointsBasedCampaign(): boolean {
    const campaign = this.currentReceiptDetail?.campaign || this.campaignInfo;
    if (!campaign) return false;

    // Check strategy type first
    if (campaign.strategy?.strategyType === 'Points') {
      return true;
    }

    // Fallback: Staff Point Strategy campaigns use points
    return campaign.isStaffPointStrategy === true;
  }

  getRewardFieldLabel(): string {
    if (this.isStampsBasedCampaign()) {
      return 'Stamps Received';
    } else if (this.isPointsBasedCampaign()) {
      return 'Points Received';
    }
    // Default to showing both or generic label
    return 'Rewards Received';
  }

  getPointsPlaceholder(): string {
    if (this.pointsAlreadyEarned > 0) {
      return `Already earned: ${this.pointsAlreadyEarned}`;
    }
    return '0';
  }

  getStampsPlaceholder(): string {
    if (this.stampsAlreadyEarned > 0) {
      return `Already earned: ${this.stampsAlreadyEarned}`;
    }
    return '0';
  }

  onPointsFocus(): void {
    this.isEditingPoints = true;
    // Clear any existing undo timer when starting to edit
    this.clearPointsUndoTimer();
    // reset invalid state when focusing
    this.isPointsInputInvalid = false;
  }

  onPointsInput(event: any): void {
    const value = event.target.value;
    const numericValue = Number(value);
    const invalid = Number.isNaN(numericValue) || numericValue <= this.pointsAlreadyEarned;
    this.pointsReceived = Number.isNaN(numericValue) ? 0 : numericValue;
    this.isPointsInputInvalid = invalid;
    if (invalid) {
      this.saveDisabled = true;
    } else {
      this.refreshPushToRepocessingStatus(this.tags);
    }
  }

  onStampsFocus(): void {
    this.isEditingStamps = true;
    // Clear any existing undo timer when starting to edit
    this.clearStampsUndoTimer();
    // reset invalid state when focusing
    this.isStampsInputInvalid = false;
  }

  onStampsInput(event: any): void {
    const value = event.target.value;
    const numericValue = Number(value);
    const invalid = Number.isNaN(numericValue) || numericValue <= this.stampsAlreadyEarned;
    this.stampsReceived = Number.isNaN(numericValue) ? 0 : numericValue;
    this.isStampsInputInvalid = invalid;
    if (invalid) {
      this.saveDisabled = true;
    } else {
      this.refreshPushToRepocessingStatus(this.tags);
    }
  }

  submitPoints(): void {
    // Require that new points must be strictly greater than already earned
    if (this.pointsReceived <= this.pointsAlreadyEarned) {
      // mark invalid and keep editing
      this.isPointsInputInvalid = true;
      return;
    }

    // valid - clear invalid state and proceed
    this.isPointsInputInvalid = false;
    this.isEditingPoints = false;
    this.hasPendingPointsSave = true;

    // Calculate and show changes
    this.onPointsStampsChange('points');

    // Start countdown
    this.pointsUndoCountdown = 20;

    // Show initial snackbar with countdown
    const snackBarRef = this.snackbar.open(
      `Points will be saved and receipt reprocessed in ${this.pointsUndoCountdown} seconds`,
      'UNDO',
      { duration: 20000, horizontalPosition: 'left' }
    );

    // Track if undo was clicked
    let undoClicked = false;
    snackBarRef.onAction().subscribe(() => {
      undoClicked = true;
      this.undoPoints();
    });

    this.pointsUndoTimer = setInterval(() => {
      this.pointsUndoCountdown--;

      // Update snackbar message with new countdown by accessing the DOM element
      if (this.pointsUndoCountdown > 0 && !undoClicked) {
        const snackBarElement = document.querySelector('.mat-mdc-snack-bar-container .mdc-snackbar__label');
        if (snackBarElement) {
          snackBarElement.textContent = `Points will be saved and receipt reprocessed in ${this.pointsUndoCountdown} second${this.pointsUndoCountdown !== 1 ? 's' : ''}`;
        }
      }

      if (this.pointsUndoCountdown <= 0) {
        this.hasPendingPointsSave = false;
        this.savePointsImmediately();
      }
    }, 1000);
    this.saveDisabled = false;
  }

  cancelPoints(): void {
    this.pointsReceived = this.originalPointsReceived;
    this.isEditingPoints = false;
    this.clearPointsUndoTimer();
    this.isPointsInputInvalid = false;
    this.refreshPushToRepocessingStatus(this.tags);
  }

  undoPoints(): void {
    this.clearPointsUndoTimer();
    this.pointsReceived = this.originalPointsReceived;
    this.hasPendingPointsSave = false;

    // Remove the manual tag that was added
    if (this.receipt.tags) {
      this.receipt.tags = this.receipt.tags.filter(tag =>
        !tag.startsWith(RECEIPT_TAGS.REPROCESS_MANUAL_GIVE_POINTS_PREFIX_TAG)
      );
      this.tags = [...this.receipt.tags];
    }

  this.snackbar.open('Points changes cancelled', null, { duration: 3000, horizontalPosition: 'left' });
  this.refreshPushToRepocessingStatus(this.tags);
  }

  clearPointsUndoTimer(): void {
    if (this.pointsUndoTimer) {
      clearInterval(this.pointsUndoTimer);
      this.pointsUndoTimer = null;
    }
    this.pointsUndoCountdown = 0;
  }

  async savePointsImmediately(): Promise<void> {
    this.clearPointsUndoTimer();

    if (!this.receipt?.id) {
      console.error('Cannot save points: receipt ID is missing');
      return;
    }

    try {
      const { lineItemData } = this.mapLineItemsForSave(this.receipt.lineItems);

      const updatedReceipt = new Receipt({
        ...this.receipt,
        lineItems: lineItemData
      });

      const request = new UpdateReceiptRequest({
        actions: this.receipt.tags || [],
        note: this.receipt.note || null,
        numberOfStamps: this.getManualGiveStampsValue(this.receipt.tags),
        receipt: updatedReceipt,
        isReprocess: true, // Reprocess the receipt to apply points
        isUpdateReceipt: true
      });

      await lastValueFrom(this.receiptApi.updateReceiptById(this.receipt.id, request));
      this.snackbar.open('Points saved and receipt reprocessed successfully', null, { duration: 3000, horizontalPosition: 'left' });

      // Store the new value as the original
      this.originalPointsReceived = this.pointsReceived;
    } catch (error) {
      console.error('Error saving points:', error);
      this.snackbar.open('Failed to save points', null, { duration: 5000, horizontalPosition: 'left' });
    }
  }

  submitStamps(): void {
    // Require that new stamps must be strictly greater than already earned
    if (this.stampsReceived <= this.stampsAlreadyEarned) {
      // mark invalid and keep editing
      this.isStampsInputInvalid = true;
      return;
    }

    // valid - clear invalid state and proceed
    this.isStampsInputInvalid = false;
    this.isEditingStamps = false;
    this.hasPendingStampsSave = true;

    // Calculate and show changes
    this.onPointsStampsChange('stamps');

    // Start countdown
    this.stampsUndoCountdown = 20;

    // Show initial snackbar with countdown
    const snackBarRef = this.snackbar.open(
      `Stamps will be saved and receipt reprocessed in ${this.stampsUndoCountdown} seconds`,
      'UNDO',
      { duration: 20000, horizontalPosition: 'left' }
    );

    // Track if undo was clicked
    let undoClicked = false;
    snackBarRef.onAction().subscribe(() => {
      undoClicked = true;
      this.undoStamps();
    });

    this.stampsUndoTimer = setInterval(() => {
      this.stampsUndoCountdown--;

      // Update snackbar message with new countdown by accessing the DOM element
      if (this.stampsUndoCountdown > 0 && !undoClicked) {
        const snackBarElement = document.querySelector('.mat-mdc-snack-bar-container .mdc-snackbar__label');
        if (snackBarElement) {
          snackBarElement.textContent = `Stamps will be saved and receipt reprocessed in ${this.stampsUndoCountdown} second${this.stampsUndoCountdown !== 1 ? 's' : ''}`;
        }
      }

      if (this.stampsUndoCountdown <= 0) {
        this.hasPendingStampsSave = false;
        this.saveStampsImmediately();
      }
    }, 1000);
    this.saveDisabled = false;
  }

  cancelStamps(): void {
    this.stampsReceived = this.originalStampsReceived;
    this.isEditingStamps = false;
    this.clearStampsUndoTimer();
    this.isStampsInputInvalid = false;
    this.refreshPushToRepocessingStatus(this.tags);
  }

  undoStamps(): void {
    this.clearStampsUndoTimer();
    this.stampsReceived = this.originalStampsReceived;
    this.hasPendingStampsSave = false;

    // Remove the manual tag that was added
    if (this.receipt.tags) {
      this.receipt.tags = this.receipt.tags.filter(tag =>
        !tag.startsWith(RECEIPT_TAGS.REPROCESS_MANUAL_GIVE_STAMPS_PREFIX_TAG)
      );
      this.tags = [...this.receipt.tags];
    }

  this.snackbar.open('Stamps changes cancelled', null, { duration: 3000, horizontalPosition: 'left' });
  this.refreshPushToRepocessingStatus(this.tags);
  }

  clearStampsUndoTimer(): void {
    if (this.stampsUndoTimer) {
      clearInterval(this.stampsUndoTimer);
      this.stampsUndoTimer = null;
    }
    this.stampsUndoCountdown = 0;
  }

  /**
   * Execute any pending saves immediately
   * Called when navigating away, switching records, or on component destroy
   */
  private executePendingSaves(): void {
    if (this.hasPendingPointsSave) {
      this.clearPointsUndoTimer();
      this.hasPendingPointsSave = false;
      this.savePointsImmediately();
    }

    if (this.hasPendingStampsSave) {
      this.clearStampsUndoTimer();
      this.hasPendingStampsSave = false;
      this.saveStampsImmediately();
    }

    if (this.hasPendingActionSave) {
      this.clearActionUndoTimer();
      this.hasPendingActionSave = false;
      this.saveActionImmediately();
    }

    if (this.hasPendingLineItemsSave) {
      this.clearLineItemsUndoTimer();
      this.hasPendingLineItemsSave = false;
      this.saveLineItemsImmediately();
    }
  }

  async saveStampsImmediately(): Promise<void> {
    this.clearStampsUndoTimer();

    if (!this.receipt?.id) {
      console.error('Cannot save stamps: receipt ID is missing');
      return;
    }

    try {
      const { lineItemData } = this.mapLineItemsForSave(this.receipt.lineItems);

      const updatedReceipt = new Receipt({
        ...this.receipt,
        lineItems: lineItemData
      });

      const request = new UpdateReceiptRequest({
        actions: this.receipt.tags || [],
        note: this.receipt.note || null,
        numberOfStamps: this.getManualGiveStampsValue(this.receipt.tags),
        receipt: updatedReceipt,
        isReprocess: true, // Reprocess the receipt to apply stamps
        isUpdateReceipt: true
      });

      await lastValueFrom(this.receiptApi.updateReceiptById(this.receipt.id, request));
  this.snackbar.open('Stamps saved and receipt reprocessed successfully', null, { duration: 3000, horizontalPosition: 'left' });

      // Store the new value as the original
      this.originalStampsReceived = this.stampsReceived;
    } catch (error) {
      console.error('Error saving stamps:', error);
  this.snackbar.open('Failed to save stamps', null, { duration: 5000, horizontalPosition: 'left' });
    }
  }

  onPointsStampsChange(type: 'points' | 'stamps') {
    const currentValue = type === 'points' ? this.pointsReceived : this.stampsReceived;
    const alreadyEarned = type === 'points' ? this.pointsAlreadyEarned : this.stampsAlreadyEarned;

    // Calculate the difference (what admin wants vs what user already received)
    const difference = currentValue - alreadyEarned;

    // Determine the tag prefix based on campaign strategy type
    const isPointsStrategy = this.isPointsBasedCampaign();
    const tagPrefix = isPointsStrategy
      ? RECEIPT_TAGS.REPROCESS_MANUAL_GIVE_POINTS_PREFIX_TAG
      : RECEIPT_TAGS.REPROCESS_MANUAL_GIVE_STAMPS_PREFIX_TAG;

    if (!this.receipt.tags) {
      this.receipt.tags = [];
    }
    this.receipt.tags = this.receipt.tags.filter(tag =>
      !tag.startsWith(RECEIPT_TAGS.REPROCESS_MANUAL_GIVE_STAMPS_PREFIX_TAG) &&
      !tag.startsWith(RECEIPT_TAGS.REPROCESS_MANUAL_GIVE_POINTS_PREFIX_TAG)
    );

    if (difference !== 0) {
      // Add new tag with the difference (can be positive for add, negative for subtract)
      const newTag = `${tagPrefix}${difference}`;
      this.receipt.tags.push(newTag);

      const rewardType = type === 'points' ? 'points' : 'stamps';
      const action = difference > 0 ? 'add' : 'subtract';
      const absValue = Math.abs(difference);

      this.snackbar.open(
        `Will ${action} ${absValue} ${rewardType} (Current: ${currentValue}, Already earned: ${alreadyEarned})`,
        null,
        { duration: 5000, horizontalPosition: 'left' }
      );
    } else {
      const rewardType = type === 'points' ? 'points' : 'stamps';
      this.snackbar.open(
        `No change needed - user already has ${alreadyEarned} ${rewardType}`,
        null,
        { duration: 3000, horizontalPosition: 'left' }
      );
    }

    // Sync component tags array with receipt tags
    this.tags = [...this.receipt.tags];

    // If using multiple receipts, sync detail object
    if (this.currentReceiptDetail && this.currentReceiptDetail.receipt) {
      this.currentReceiptDetail.receipt.tags = this.receipt.tags;
    }

    // Update receipt data
    const receiptId = this.receipt?.id || 'default';
    this.updateReceiptData(receiptId, {
      pointsReceived: this.pointsReceived,
      stampsReceived: this.stampsReceived,
      originalPointsReceived: this.pointsReceived,
      originalStampsReceived: this.stampsReceived
    });

    this.tagsChangedHandler(true);
  }

  onNoteFocus(): void {
    this.isEditingNote = true;
    this.originalNote = this.mainForm.get('note')?.value || '';
  }

  onNoteInput(event: any): void {
    const newNote = event.target.value;
    this.mainForm.get('note')?.setValue(newNote, { emitEvent: false });

    // Update the receipt note in real-time for display
    if (this.currentReceiptDetail?.receipt) {
      this.currentReceiptDetail.receipt.note = newNote;
    }
    if (this.receipt) {
      this.receipt.note = newNote;
    }
  }

  async submitNote(): Promise<void> {
    const currentNote = this.mainForm.get('note')?.value || '';

    // Check if note actually changed
    if (currentNote === this.originalNote) {
      this.snackbar.open('No changes to save', null, { duration: 2000, horizontalPosition: 'left' });
      this.isEditingNote = false;
      return;
    }

    this.isEditingNote = false;
    await this.saveNoteImmediately();
  }

  cancelNote(): void {
    // Restore original note
    this.mainForm.get('note')?.setValue(this.originalNote, { emitEvent: false });

    if (this.currentReceiptDetail?.receipt) {
      this.currentReceiptDetail.receipt.note = this.originalNote;
    }
    if (this.receipt) {
      this.receipt.note = this.originalNote;
    }

    this.isEditingNote = false;
  }

  private async saveNoteImmediately(): Promise<void> {
    if (!this.receipt?.id) {
      console.error('Cannot save note: receipt ID is missing');
      return;
    }

    try {
      const currentNote = this.mainForm.get('note')?.value || '';
      const { lineItemData } = this.mapLineItemsForSave(this.receipt.lineItems);

      const updatedReceipt = new Receipt({
        ...this.receipt,
        note: currentNote,
        lineItems: lineItemData
      });

      const request = new UpdateReceiptRequest({
        actions: this.receipt.tags || [],
        note: currentNote,
        numberOfStamps: this.getNumberOfStampsForPayload(this.receipt?.id),
        receipt: updatedReceipt,
        isReprocess: false,
        isUpdateReceipt: true
      });

      await lastValueFrom(this.receiptApi.updateReceiptById(this.receipt.id, request));

  this.snackbar.open('Admin note saved successfully', null, { duration: 3000, horizontalPosition: 'left' });

      // Update original note so we don't mark as changed anymore
      this.originalNote = currentNote;
      const receiptId = this.receipt.id;
      const savedData = this.receiptDataMap.get(receiptId);
      if (savedData) {
        savedData.originalNote = currentNote;
        savedData.note = currentNote;
        this.receiptDataMap.set(receiptId, savedData);
      }
    } catch (error) {
      console.error('Failed to save admin note:', error);
  this.snackbar.open('Failed to save admin note', null, { duration: 3000, horizontalPosition: 'left' });
    }
  }

  onNoteChange(event: any, detail: ReceiptDetail) {
    const newNote = event.target.value;
    detail.receipt.note = newNote;
    this.mainForm.get('note')?.setValue(newNote, { emitEvent: false });
  }

  patchFormValues(lineItems: any[]) {
    const lineItemsFormArray = this.lineItemsArray;
    lineItemsFormArray.clear();

    if (lineItems && lineItems.length > 0) {
      lineItems.forEach(item => {
        lineItemsFormArray.push(this.createLineItemFormGroup(item));
      });
    }
  }

  createLineItemFormGroup(item: any): FormGroup {
    const formGroup = new FormGroup({
      id: new FormControl(item.id),
      category: new FormControl(item.category),
      description: new FormControl(item.description, [Validators.required, Validators.maxLength(255)]),
      quantity: new FormControl(item.quantity ?? 0, [Validators.required, Validators.min(0)]),
      price: new FormControl(item.price ?? 0, [Validators.required, Validators.min(0)]),
      discount: new FormControl(item.discount ?? 0, [Validators.required, Validators.min(0)]),
      total: new FormControl(item.total ?? 0, [Validators.required, Validators.min(0)]),
      _editing: new FormControl(false),
      _originalValue: new FormControl(null)
    });

    // Prevent negative values
    ['quantity', 'price', 'discount', 'total'].forEach(field => {
      formGroup.get(field)?.valueChanges.subscribe(value => {
        if (value < 0) {
          formGroup.get(field)?.setValue(0, { emitEvent: false });
        }
      });
    });

    return formGroup;
  }

  addLineItem(): void {
    const newItem = this.createLineItemFormGroup({
      id: null,
      category: '',
      description: '',
      quantity: 0,
      price: 0,
      discount: 0,
      total: 0
    });
    newItem.patchValue({ _editing: true });
    this.lineItemsArray.push(newItem);

    this.updateReceiptLineItems();
  }

  editLineItem(index: number): void {
    const item = this.lineItemsArray.at(index);
    item.patchValue({
      _editing: true,
      _originalValue: item.value
    });
  }

  saveLineItem(index: number): void {
    const item = this.lineItemsArray.at(index);
    const description = item.get('description')?.value;

    // Validate description is not null or empty
    if (!description || description.trim() === '') {
      this.snackbar.open('Description is required', null, { duration: 3000, horizontalPosition: 'left' });
      item.get('description')?.markAsTouched();
      return;
    }

    // Ensure all numeric fields are non-negative
    ['quantity', 'price', 'discount', 'total'].forEach(field => {
      const value = item.get(field)?.value;
      if (value < 0) {
        item.get(field)?.setValue(0, { emitEvent: false });
      }
    });

    // Clear any existing undo timer
    this.clearLineItemsUndoTimer();

    // Store original line items for undo
    this.originalLineItemsForUndo = this.cloneLineItems(this.getFormLineItems());

    item.patchValue({
      _editing: false,
      _originalValue: null
    });

    // Update receipt line items
    this.updateReceiptLineItems();

    // Mark as pending save
    this.hasPendingLineItemsSave = true;

    // Start countdown
    this.lineItemsUndoCountdown = 20;

    // Show snackbar with countdown
    const snackBarRef = this.snackbar.open(
      `Line items will be saved in ${this.lineItemsUndoCountdown} seconds`,
      'UNDO',
      { duration: 20000, horizontalPosition: 'left' }
    );

    // Track if undo was clicked
    let undoClicked = false;
    snackBarRef.onAction().subscribe(() => {
      undoClicked = true;
      this.undoLineItems();
    });

    this.lineItemsUndoTimer = setInterval(() => {
      this.lineItemsUndoCountdown--;

      if (this.lineItemsUndoCountdown > 0) {
        // Update snackbar text without dismissing it
        const snackbarElement = document.querySelector('.mdc-snackbar__label');
        if (snackbarElement) {
          snackbarElement.textContent = `Line items will be saved in ${this.lineItemsUndoCountdown} seconds`;
        }
      } else {
        this.clearLineItemsUndoTimer();
        if (!undoClicked) {
          this.saveLineItemsImmediately();
        }
      }
    }, 1000);
  }

  cancelEditLineItem(index: number): void {
    const item = this.lineItemsArray.at(index);
    const originalValue = item.get('_originalValue')?.value;

    if (originalValue) {
      item.patchValue(originalValue);
      item.patchValue({ _editing: false, _originalValue: null });
    } else {
      // New item that hasn't been saved yet
      this.lineItemsArray.removeAt(index);
      // Update receipt line items after removal
      this.updateReceiptLineItems();
    }
  }

  undoLineItems(): void {
    this.clearLineItemsUndoTimer();
    this.hasPendingLineItemsSave = false;

    // Restore original line items
    if (this.originalLineItemsForUndo.length > 0) {
      this.patchFormValues(this.originalLineItemsForUndo);
      this.updateReceiptLineItems();
    }

    this.snackbar.open('Line items changes cancelled', null, { duration: 3000, horizontalPosition: 'left' });
  }

  clearLineItemsUndoTimer(): void {
    if (this.lineItemsUndoTimer) {
      clearInterval(this.lineItemsUndoTimer);
      this.lineItemsUndoTimer = null;
    }
    this.lineItemsUndoCountdown = 0;
  }

  async saveLineItemsImmediately(): Promise<void> {
    this.clearLineItemsUndoTimer();

    if (!this.receipt?.id) {
      console.error('No receipt ID available');
      return;
    }

    try {
      const formLineItems = this.getFormLineItems();
      const { lineItemData, rawLineItems } = this.mapLineItemsForSave(formLineItems);

      // Check if there are multiple receipts (multiple campaign tabs)
      if (this.receiptDetails.length > 1) {
        // Update all receipts with the same line items
        const updatePromises = this.receiptDetails.map(async (detail) => {
          if (!detail.receipt?.id) {
            return;
          }

          // Update raw JSON
          if (detail.receipt.rawJson) {
            const rawData = JSON.parse(detail.receipt.rawJson);
            rawData.line_items = rawLineItems;
            detail.receipt.rawJson = JSON.stringify(rawData);
          }

          // Update receipt line items
          detail.receipt.lineItems = lineItemData;

          // Save to API
          const request = new UpdateReceiptRequest({
            actions: detail.receipt.tags || [],
            note: detail.receipt.note || '',
            numberOfStamps: this.getNumberOfStampsForPayload(detail.receipt?.id),
            receipt: detail.receipt,
            isReprocess: false,
            isUpdateReceipt: true
          });

          return lastValueFrom(this.receiptApi.updateReceiptById(detail.receipt.id, request));
        });

        await Promise.all(updatePromises);

        this.snackbar.open(`Line items saved successfully for ${this.receiptDetails.length} receipts`, null, { duration: 3000, horizontalPosition: 'left' });
      } else {
        // Single receipt - update only the current one
        const receiptId = this.receipt.id;

        // Update raw JSON
        if (this.receipt.rawJson) {
          const rawData = JSON.parse(this.receipt.rawJson);
          rawData.line_items = rawLineItems;
          this.receipt.rawJson = JSON.stringify(rawData);
        } else {
          // Create new raw JSON structure if it doesn't exist
          this.raw.line_items = rawLineItems;
          this.receipt.rawJson = JSON.stringify(this.raw);
        }

        // Update receipt line items
        this.receipt.lineItems = lineItemData;

        // Save to API
        const request = new UpdateReceiptRequest({
          actions: this.receipt.tags || [],
          note: this.mainForm.get('note')?.value || '',
          numberOfStamps: this.getNumberOfStampsForPayload(this.receipt?.id),
          receipt: this.receipt,
          isReprocess: false,
          isUpdateReceipt: true
        });

        await lastValueFrom(this.receiptApi.updateReceiptById(receiptId, request));

        this.snackbar.open('Line items saved successfully', null, { duration: 3000, horizontalPosition: 'left' });
      }

      // Mark as no longer pending
      this.hasPendingLineItemsSave = false;

      // Update original line items for future comparisons
      this.originalLineItemsForUndo = this.cloneLineItems(formLineItems);

      // Reload receipt to get fresh data
      await this.reloadReceipt();
    } catch (error) {
      console.error('Error saving line items:', error);
      this.snackbar.open('Failed to save line items', null, { duration: 5000, horizontalPosition: 'left' });
    }
  }

  async deleteLineItem(index: number) {
    const dialogModel = ConfirmDialogModel.danger(
      'This action cannot be undone. Are you absolutely sure?',
      'Delete Line Item?'
    );
    const confirmResult = await dialogModel.show(this.dialog);
    if (!confirmResult) {
      return;
    }

    // Clear any existing undo timer
    this.clearLineItemsUndoTimer();

    // Store original line items for undo
    this.originalLineItemsForUndo = this.cloneLineItems(this.getFormLineItems());

    this.lineItemsArray.removeAt(index);

    // Update receipt line items
    this.updateReceiptLineItems();

    // Mark as pending save
    this.hasPendingLineItemsSave = true;

    // Start countdown
    this.lineItemsUndoCountdown = 20;

    // Show snackbar with countdown
    const snackBarRef = this.snackbar.open(
      `Line item deleted. Changes will be saved in ${this.lineItemsUndoCountdown} seconds`,
      'UNDO',
      { duration: 20000, horizontalPosition: 'left' }
    );

    // Track if undo was clicked
    let undoClicked = false;
    snackBarRef.onAction().subscribe(() => {
      undoClicked = true;
      this.undoLineItems();
    });

    this.lineItemsUndoTimer = setInterval(() => {
      this.lineItemsUndoCountdown--;

      if (this.lineItemsUndoCountdown > 0) {
        // Update snackbar text without dismissing it
        const snackbarElement = document.querySelector('.mdc-snackbar__label');
        if (snackbarElement) {
          snackbarElement.textContent = `Line item deleted. Changes will be saved in ${this.lineItemsUndoCountdown} seconds`;
        }
      } else {
        this.clearLineItemsUndoTimer();
        if (!undoClicked) {
          this.saveLineItemsImmediately();
        }
      }
    }, 1000);
  }

  async removeAllLineItems(): Promise<void> {
    const dialogModel = ConfirmDialogModel.danger(
      'This action cannot be undone. Are you absolutely sure?',
      'Remove All Line Items?'
    );
    const confirmResult = await dialogModel.show(this.dialog);
    if (!confirmResult) {
      return;
    }

    // Clear any existing undo timer
    this.clearLineItemsUndoTimer();

    // Store original line items for undo
    this.originalLineItemsForUndo = this.cloneLineItems(this.getFormLineItems());

    this.lineItemsArray.clear();

    // Update receipt line items
    this.updateReceiptLineItems();

    // Mark as pending save
    this.hasPendingLineItemsSave = true;

    // Start countdown
    this.lineItemsUndoCountdown = 20;

    // Show snackbar with countdown
    const snackBarRef = this.snackbar.open(
      `All line items removed. Changes will be saved in ${this.lineItemsUndoCountdown} seconds`,
      'UNDO',
      { duration: 20000, horizontalPosition: 'left' }
    );

    // Track if undo was clicked
    let undoClicked = false;
    snackBarRef.onAction().subscribe(() => {
      undoClicked = true;
      this.undoLineItems();
    });

    this.lineItemsUndoTimer = setInterval(() => {
      this.lineItemsUndoCountdown--;

      if (this.lineItemsUndoCountdown > 0) {
        // Update snackbar text without dismissing it
        const snackbarElement = document.querySelector('.mdc-snackbar__label');
        if (snackbarElement) {
          snackbarElement.textContent = `All line items removed. Changes will be saved in ${this.lineItemsUndoCountdown} seconds`;
        }
      } else {
        this.clearLineItemsUndoTimer();
        if (!undoClicked) {
          this.saveLineItemsImmediately();
        }
      }
    }, 1000);
  }

  private updateReceiptLineItems(): void {
    const formLineItems = this.getFormLineItems();

    // Convert form line items to LineItem objects
    const lineItemData = formLineItems.map(
      (lineItem: any) => new LineItem({
        name: lineItem.description,
        amount: lineItem.quantity ?? 0,
        price: lineItem.price ?? 0,
        discount: lineItem.discount ?? 0,
        total: lineItem.total ?? 0
      })
    );

    // Update raw JSON data
    const rawLineItems = formLineItems;

    // Update ALL receipts with the same docId (all receipts in receiptDetails share same docId)
    this.receiptDetails.forEach(detail => {
      if (detail.receipt) {
        // Update line items
        detail.receipt.lineItems = lineItemData;

        // Update raw JSON if it exists
        if (detail.receipt.rawJson) {
          try {
            const raw = JSON.parse(detail.receipt.rawJson);
            raw.line_items = rawLineItems;
            raw.invoice_number = this.invoiceNumber;
            raw.date = this.invoiceDateIso;
            detail.receipt.rawJson = JSON.stringify(raw);
          } catch (error) {
            console.error('Failed to update raw JSON for receipt:', detail.receipt.id, error);
          }
        }

        // Update in receiptDataMap
        const receiptId = detail.receipt.id || '';
        if (this.receiptDataMap.has(receiptId)) {
          this.updateReceiptData(receiptId, { lineItems: formLineItems });
        }
      }
    });

    // Update current receipt
    this.receipt.lineItems = lineItemData;
    if (this.receipt.rawJson) {
      try {
        const raw = JSON.parse(this.receipt.rawJson);
        raw.line_items = rawLineItems;
        raw.invoice_number = this.invoiceNumber;
        raw.date = this.invoiceDateIso;
        this.receipt.rawJson = JSON.stringify(raw);
        this.raw = raw;
      } catch (error) {
        console.error('Failed to update raw JSON:', error);
      }
    }

    // Save changes to current campaign data map
    this.saveCampaignChanges();
  }

  // Tags Management
  addTag() {
    if (this.newTag.trim()) {
      const newTagValue = this.newTag.trim();

      // Add to receipt tags (primary source)
      if (!this.receipt.tags) {
        this.receipt.tags = [];
      }
      if (!this.receipt.tags.includes(newTagValue)) {
        this.receipt.tags.push(newTagValue);
      }

      // Sync component tags array with receipt tags
      this.tags = [...this.receipt.tags];

      // If using multiple receipts, ensure detail object references the same array
      if (this.currentReceiptDetail && this.currentReceiptDetail.receipt) {
        this.currentReceiptDetail.receipt.tags = this.receipt.tags;
      }

      this.newTag = '';
      this.updateTagChecks(false);
      this.saveCampaignChanges();
    }
  }

  removeTag(tag: string) {
    // Remove from receipt tags (primary source)
    if (this.receipt.tags) {
      this.receipt.tags = this.receipt.tags.filter(t => t !== tag);
    }

    // Sync component tags array with receipt tags
    this.tags = this.receipt.tags ? [...this.receipt.tags] : [];

    // If using multiple receipts, ensure detail object references the same array
    if (this.currentReceiptDetail && this.currentReceiptDetail.receipt) {
      this.currentReceiptDetail.receipt.tags = this.receipt.tags;
    }

    this.updateTagChecks(false);
    this.saveCampaignChanges();
  }

  toggleTagEditMode() {
    this.isTagEditMode = !this.isTagEditMode;
    if (!this.isTagEditMode) {
      // Clear the input when closing edit mode
      this.newTag = '';
      this.filteredTagSuggestions = [];
    } else {
      // Show all suggestions when opening edit mode
      this.filteredTagSuggestions = [...this.tagSuggestions];
    }
  }

  filterTagSuggestions() {
    const filterValue = this.newTag.toLowerCase().trim();

    if (!filterValue) {
      // Show all suggestions if input is empty
      this.filteredTagSuggestions = [...this.tagSuggestions];
    } else {
      // Filter suggestions based on input
      this.filteredTagSuggestions = this.tagSuggestions.filter(tag =>
        tag.toLowerCase().includes(filterValue)
      );
    }
  }

  onTagSelected(event: any) {
    // When a tag is selected from autocomplete, add it immediately
    const selectedTag = event.option.value;
    this.newTag = selectedTag;
    this.addTag();
  }

  openChangelogDialog() {
    this.dialog.open(ChangelogHistoryDialogComponent, {
      width: '90vw',
      maxWidth: '1200px',
      height: '80vh',
      data: {
        changes: this.receiptChanges,
        columns: this.columns,
        receiptId: this.receipt?.id,
        campaignTitle: this.campaignInfo?.title
      }
    });
  }

  async onSave() {
    this.saveCampaignChanges();

    if (this.receiptDetails.length > 1) {
      await this.showApplyChangesDialog('save', this.saveText);
      return;
    }

    await this.submit();
  }

  // Action Buttons
  giveStamps() {
    const numberOfStampsControl = this.mainForm.get('numberOfStamps');
    const stamps = numberOfStampsControl?.value;

    const newTag = `${RECEIPT_TAGS.REPROCESS_MANUAL_GIVE_STAMPS_PREFIX_TAG}${stamps}`;
    this.removeTagPreviousAction();

    if (!this.tags.some(tag => tag.startsWith(RECEIPT_TAGS.REPROCESS_MANUAL_GIVE_STAMPS_PREFIX_TAG))) {
      if (!stamps || stamps === 0) {
        numberOfStampsControl?.addValidators(Validators.required);
        numberOfStampsControl?.updateValueAndValidity();
        this.snackbar.open('Please enter number of stamps', null, { duration: 3000, horizontalPosition: 'left' });
        return;
      } else {
        this.tags.push(newTag);
        this.isGiveStampsChecked = true;
        this.rejectDisabled = true;
      }
    } else {
      this.tags = this.tags.filter(item => !item.startsWith(RECEIPT_TAGS.REPROCESS_MANUAL_GIVE_STAMPS_PREFIX_TAG));
      numberOfStampsControl?.setValue(null);
      numberOfStampsControl?.clearValidators();
      numberOfStampsControl?.updateValueAndValidity();
      this.isGiveStampsChecked = false;
      this.rejectDisabled = false;
    }
    this.tagsChangedHandler(true);
  }

  async checkEligible() {
    if (this.receiptDetails.length > 1) {
      await this.showApplyChangesDialog('eligible', 'Mark as Eligible');
    } else {
      // Store original tags before making changes
      this.originalTags = [...(this.receipt.tags || [])];
      this.currentActionType = 'eligible';
      this.hasPendingActionSave = true;
      this.applyEligibleToCampaign();
      this.startActionUndoTimer('eligible');
    }
  }

  async checkFraud() {
    if (this.receiptDetails.length > 1) {
      await this.showApplyChangesDialog('fraud', 'Mark as Fraud');
    } else {
      // Store original tags before making changes
      this.originalTags = [...(this.receipt.tags || [])];
      this.currentActionType = 'fraud';
      this.hasPendingActionSave = true;
      this.applyFraudToCampaign();
      this.startActionUndoTimer('fraud');
    }
  }

  async reject() {
    if (this.receiptDetails.length > 1) {
      await this.showApplyChangesDialog('reject', 'Reject');
    } else {
      // Store original tags before making changes
      this.originalTags = [...(this.receipt.tags || [])];
      this.currentActionType = 'reject';
      this.hasPendingActionSave = true;
      this.applyRejectToCampaign();
      this.startActionUndoTimer('reject');
    }
  }

  startActionUndoTimer(actionType: 'eligible' | 'fraud' | 'reject'): void {
    this.clearActionUndoTimer();
    this.showActionUndoButton = true;
    this.actionUndoCountdown = 20;

    // Disable all action buttons
    this.checkEligibleDisabled = true;
    this.checkFraudDisabled = true;
    this.rejectDisabled = true;

    const messages = {
      eligible: `Receipt will be marked as eligible and processed in ${this.actionUndoCountdown} seconds`,
      fraud: `Receipt will be marked as fraud and processed in ${this.actionUndoCountdown} seconds`,
      reject: `Receipt will be rejected and processed in ${this.actionUndoCountdown} seconds`
    };

    const snackBarRef = this.snackbar.open(
      messages[actionType],
      'UNDO',
      { duration: 20000, horizontalPosition: 'left' }
    );

    // Track if undo was clicked
    let undoClicked = false;
    snackBarRef.onAction().subscribe(() => {
      undoClicked = true;
      this.undoAction();
    });

    this.actionUndoTimer = setInterval(() => {
      this.actionUndoCountdown--;

      // Update snackbar message with new countdown
      if (this.actionUndoCountdown > 0 && !undoClicked) {
        const snackBarElement = document.querySelector('.mat-mdc-snack-bar-container .mdc-snackbar__label');
        if (snackBarElement) {
          const actionMessages = {
            eligible: `Receipt will be marked as eligible and processed in ${this.actionUndoCountdown} second${this.actionUndoCountdown !== 1 ? 's' : ''}`,
            fraud: `Receipt will be marked as fraud and processed in ${this.actionUndoCountdown} second${this.actionUndoCountdown !== 1 ? 's' : ''}`,
            reject: `Receipt will be rejected and processed in ${this.actionUndoCountdown} second${this.actionUndoCountdown !== 1 ? 's' : ''}`
          };
          snackBarElement.textContent = actionMessages[actionType];
        }
      }

      if (this.actionUndoCountdown <= 0) {
        this.hasPendingActionSave = false;
        this.saveActionImmediately();
      }
    }, 1000);
  }

  undoAction(): void {
    // Check if this is a multi-campaign action
    if (this.selectedCampaignIdsForUndo.length > 0) {
      this.undoMultiCampaignAction();
      return;
    }

    // Single campaign undo
    this.clearActionUndoTimer();
    this.hasPendingActionSave = false;

    // Restore original tags
    if (this.receipt.tags) {
      this.receipt.tags = [...this.originalTags];
      this.tags = [...this.originalTags];
    }

    // If using multiple receipts, sync detail object
    if (this.currentReceiptDetail && this.currentReceiptDetail.receipt) {
      this.currentReceiptDetail.receipt.tags = [...this.originalTags];
    }

    this.updateTagChecks(false);

    // Re-enable all action buttons
    this.checkEligibleDisabled = false;
    this.checkFraudDisabled = false;
    this.rejectDisabled = false;

    this.currentActionType = null;
    this.snackbar.open('Decision undone', null, { duration: 3000, horizontalPosition: 'left' });
  }

  clearActionUndoTimer(): void {
    if (this.actionUndoTimer) {
      clearInterval(this.actionUndoTimer);
      this.actionUndoTimer = null;
    }
    this.showActionUndoButton = false;
    this.actionUndoCountdown = 0;
  }

  async saveActionImmediately(): Promise<void> {
    this.clearActionUndoTimer();

    // Check if this is a multi-campaign action
    if (this.selectedCampaignIdsForUndo.length > 0) {
      // Multi-campaign action - now call the API
      if (this.currentActionType) {
        await this.applyChangesToMultipleCampaigns(this.selectedCampaignIdsForUndo, this.currentActionType);

        const messages = {
          eligible: 'Receipts marked as eligible successfully',
          fraud: 'Receipts marked as fraud successfully',
          reject: 'Receipts rejected successfully'
        };

        const message = messages[this.currentActionType] || 'Action completed successfully';
        this.snackbar.open(message, null, { duration: 3000, horizontalPosition: 'left' });
      }

      // Re-enable all action buttons
      this.checkEligibleDisabled = false;
      this.checkFraudDisabled = false;
      this.rejectDisabled = false;

      this.multiCampaignOriginalState.clear();
      this.selectedCampaignIdsForUndo = [];
      this.currentActionType = null;
      return;
    }

    // Single campaign action
    // Re-enable all action buttons
    this.checkEligibleDisabled = false;
    this.checkFraudDisabled = false;
    this.rejectDisabled = false;

    if (!this.receipt?.id) {
      console.error('Cannot save action: receipt ID is missing');
      return;
    }

    try {
      await this.submitSingleReceipt();

      const messages = {
        eligible: 'Receipt marked as eligible successfully',
        fraud: 'Receipt marked as fraud successfully',
        reject: 'Receipt rejected successfully'
      };

      const message = this.currentActionType ? messages[this.currentActionType] : 'Action completed successfully';
      this.snackbar.open(message, null, { duration: 3000, horizontalPosition: 'left' });
      this.currentActionType = null;
    } catch (error) {
      console.error('Error saving action:', error);
      this.snackbar.open('Failed to save action', null, { duration: 5000, horizontalPosition: 'left' });
    }
  }

  startMultiCampaignActionUndoTimer(actionType: 'eligible' | 'fraud' | 'reject', campaignCount: number): void {
    this.clearActionUndoTimer();
    this.showActionUndoButton = true;
    this.actionUndoCountdown = 20;

    // Disable all action buttons
    this.checkEligibleDisabled = true;
    this.checkFraudDisabled = true;
    this.rejectDisabled = true;

    this.actionUndoTimer = setInterval(() => {
      this.actionUndoCountdown--;
      if (this.actionUndoCountdown <= 0) {
        this.saveActionImmediately();
      }
    }, 1000);

    const messages = {
      eligible: `${campaignCount} receipt(s) will be marked as eligible in 20 seconds`,
      fraud: `${campaignCount} receipt(s) will be marked as fraud in 20 seconds`,
      reject: `${campaignCount} receipt(s) will be rejected in 20 seconds`
    };

    const snackBarRef = this.snackbar.open(
      messages[actionType],
      'UNDO',
      { duration: 20000, horizontalPosition: 'left' }
    );

    snackBarRef.onAction().subscribe(() => {
      this.undoMultiCampaignAction();
    });
  }

  undoMultiCampaignAction(): void {
    this.clearActionUndoTimer();

    if (this.selectedCampaignIdsForUndo.length === 0) {
      this.snackbar.open('No action to undo', null, { duration: 3000, horizontalPosition: 'left' });
      return;
    }

    // Re-enable all action buttons
    this.checkEligibleDisabled = false;
    this.checkFraudDisabled = false;
    this.rejectDisabled = false;

    // Clean up
    this.multiCampaignOriginalState.clear();
    this.selectedCampaignIdsForUndo = [];
    this.currentActionType = null;

    this.snackbar.open('Decision undone', null, { duration: 3000, horizontalPosition: 'left' });
  }

  private applyEligibleToCampaign() {
    const newTag = RECEIPT_TAGS.REPROCESS_MANUAL_ACCEPT_TAG;
    this.removeTagPreviousAction();

    if (!this.receipt.tags) {
      this.receipt.tags = [];
    }

    if (!this.receipt.tags.includes(newTag)) {
      this.receipt.tags.push(newTag);
      this.isCheckEligibleChecked = true;
      this.rejectDisabled = true;
    } else {
      this.receipt.tags = this.receipt.tags.filter(item => item !== newTag);
      this.isCheckEligibleChecked = false;
      this.rejectDisabled = false;
    }

    // Sync component tags array with receipt tags
    this.tags = [...this.receipt.tags];

    // If using multiple receipts, sync detail object
    if (this.currentReceiptDetail && this.currentReceiptDetail.receipt) {
      this.currentReceiptDetail.receipt.tags = this.receipt.tags;
    }

    this.tagsChangedHandler(true);
  }

  private applyFraudToCampaign() {
    const tagName = RECEIPT_TAGS.REPROCESS_CHECK_FRAUD;

    // Remove any conflicting previous action tags
    this.removeTagPreviousAction();

    if (!this.receipt.tags) {
      this.receipt.tags = [];
    }

    if (!this.receipt.tags.includes(tagName)) {
      this.receipt.tags.push(tagName);
      this.isCheckFraudChecked = true;
      this.rejectDisabled = true;
      this.checkEligibleDisabled = true;
      this.giveStampDisabled = true;
    } else {
      this.receipt.tags = this.receipt.tags.filter(item => item !== tagName);
      this.isCheckFraudChecked = false;
      this.rejectDisabled = false;
      this.checkEligibleDisabled = false;
      this.giveStampDisabled = false;
    }

    // Sync component tags array with receipt tags
    this.tags = [...this.receipt.tags];

    // If using multiple receipts, sync detail object
    if (this.currentReceiptDetail && this.currentReceiptDetail.receipt) {
      this.currentReceiptDetail.receipt.tags = this.receipt.tags;
    }

    this.tagsChangedHandler(true);
  }

  private applyRejectToCampaign() {
    const tagName = RECEIPT_TAGS.REPROCESS_MANUAL_REJECT_TAG;
    this.removeTagPreviousAction();

    if (!this.receipt.tags) {
      this.receipt.tags = [];
    }

    if (!this.receipt.tags.includes(tagName)) {
      this.receipt.tags.push(tagName);
      this.isRejectChecked = true;
      this.giveStampDisabled = true;
      this.checkFraudDisabled = true;
      this.checkEligibleDisabled = true;
    } else {
      this.receipt.tags = this.receipt.tags.filter(item => item !== tagName);
      this.isRejectChecked = false;
      this.giveStampDisabled = false;
      this.checkFraudDisabled = false;
      this.checkEligibleDisabled = false;
    }

    // Sync component tags array with receipt tags
    this.tags = [...this.receipt.tags];

    // If using multiple receipts, sync detail object
    if (this.currentReceiptDetail && this.currentReceiptDetail.receipt) {
      this.currentReceiptDetail.receipt.tags = this.receipt.tags;
    }

    this.tagsChangedHandler(true);
  }

  private async showApplyChangesDialog(
    actionType: 'eligible' | 'fraud' | 'reject' | 'save',
    actionLabel: string
  ): Promise<void> {
    const dialogData: ApplyChangesDialogData = {
      campaigns: this.receiptDetails.map(detail => {
        const receiptId = detail.receipt?.id || '';
        const savedData = this.receiptDataMap.get(receiptId);
        const perCampaignAction = actionType === 'save'
          ? this.resolveCampaignActionLabel(savedData)
          : actionLabel.toLowerCase();
        return {
          campaignId: detail.campaign?.campaignId || '',
          campaignTitle: detail.campaign?.title || 'Unknown Campaign',
          receiptId: receiptId,
          hasChanges: savedData?.hasChanges || false,
          actionLabel: perCampaignAction
        };
      }),
      actionType,
      actionLabel
    };

  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1920;
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 1080;
  const drawerWidth = this.parentPanel?.drawerWidth ?? 650;
  const dialogWidth = 329;
  const dialogHeight = 345;
  const horizontalGap = 8;
  const verticalGap = 24;
  const calculatedLeft = viewportWidth - drawerWidth - dialogWidth - horizontalGap;
  const leftPosition = Math.max(horizontalGap, calculatedLeft);
  const topPosition = Math.max(verticalGap, viewportHeight - dialogHeight - verticalGap);

    const dialogRef = this.dialog.open(ApplyChangesDialogComponent, {
      data: dialogData,
      width: `${dialogWidth}px`,
      disableClose: false,
      panelClass: 'apply-changes-dialog-panel',
      backdropClass: 'apply-changes-dialog-backdrop',
      position: {
        top: `${topPosition}px`,
        left: `${leftPosition}px`
      }
    });

    const result: ApplyChangesDialogResult | null = await lastValueFrom(dialogRef.afterClosed());

    if (result && result.selectedCampaignIds.length > 0) {
      // Store original state and start undo timer instead of immediately applying
      if (actionType !== 'save') {
        this.prepareMultiCampaignUndo(result.selectedCampaignIds, actionType);
      } else {
        // For save action, apply immediately without undo
        await this.applyChangesToMultipleCampaigns(result.selectedCampaignIds, actionType);
      }
    }
  }

  private prepareMultiCampaignUndo(
    campaignIds: string[],
    actionType: 'eligible' | 'fraud' | 'reject'
  ): void {
    // Store original state for undo
    this.multiCampaignOriginalState.clear();
    this.selectedCampaignIdsForUndo = [...campaignIds];
    this.currentActionType = actionType;

    campaignIds.forEach(campaignId => {
      const detail = this.receiptDetails.find(d => d.campaign?.campaignId === campaignId);
      if (detail && detail.receipt) {
        const receiptId = detail.receipt.id || '';
        const changes = this.receiptDataMap.get(receiptId);

        this.multiCampaignOriginalState.set(receiptId, {
          tags: changes?.tags ? [...changes.tags] : (detail.receipt.tags ? [...detail.receipt.tags] : []),
          note: changes?.note ?? detail.receipt.note ?? '',
          lineItems: changes?.lineItems ? this.cloneLineItems(changes.lineItems) :
                     (detail.receipt.lineItems ? this.cloneLineItems(detail.receipt.lineItems) : []),
          numberOfStamps: changes?.numberOfStamps ?? null
        });
      }
    });

    // Start undo timer - API will be called only after countdown finishes
    this.startMultiCampaignActionUndoTimer(actionType, campaignIds.length);
  }

  private async applyChangesToMultipleCampaigns(
    campaignIds: string[],
    actionType: 'eligible' | 'fraud' | 'reject' | 'save'
  ): Promise<void> {
    this.setButtonDisable(true);

    const updatePromises = campaignIds.map(async (campaignId) => {
      const detail = this.receiptDetails.find(d => d.campaign?.campaignId === campaignId);
      if (!detail || !detail.receipt) return;

      // Get the tracked changes for this specific receipt
      const receiptId = detail.receipt.id || '';
      let changes = this.receiptDataMap.get(receiptId);

      if (!changes) {
        const fallbackRewards = this.parseManualRewards(detail.receipt.tags || []);
        // Initialize tracking data when user applies action without visiting the tab
        this.updateReceiptData(receiptId, {
          tags: detail.receipt.tags ? [...detail.receipt.tags] : [],
          note: detail.receipt.note || '',
          lineItems: detail.receipt.lineItems ? this.cloneLineItems(detail.receipt.lineItems) : [],
          pointsReceived: fallbackRewards.points,
          stampsReceived: fallbackRewards.stamps,
          originalPointsReceived: fallbackRewards.points,
          originalStampsReceived: fallbackRewards.stamps,
          numberOfStamps: fallbackRewards.stamps ?? null,
          originalTags: detail.receipt.tags ? [...detail.receipt.tags] : [],
          originalNote: detail.receipt.note || '',
          originalLineItems: detail.receipt.lineItems ? this.cloneLineItems(detail.receipt.lineItems) : [],
          originalNumberOfStamps: null
        });
        changes = this.receiptDataMap.get(receiptId);
      }

      if (!changes) return;

      const lineItemSource = (changes.lineItems && changes.lineItems.length > 0)
        ? changes.lineItems
        : (detail.receipt.lineItems ? this.cloneLineItems(detail.receipt.lineItems) : []);

      const { lineItemData, rawLineItems } = this.mapLineItemsForSave(lineItemSource);

      // Clone and update receipt
      const updatedReceipt = new Receipt(detail.receipt);
      updatedReceipt.lineItems = lineItemData;

      // Update raw JSON if needed
      if (updatedReceipt.rawJson) {
        try {
          const raw = JSON.parse(updatedReceipt.rawJson);
          raw.line_items = rawLineItems;
          updatedReceipt.rawJson = JSON.stringify(raw);
        } catch (error) {
          console.error('Failed to update raw JSON for receipt:', detail.receipt.id, error);
        }
      }

      if (actionType === 'save') {
        if (!changes.hasChanges) {
          return;
        }

        const saveTags = changes.tags ? [...changes.tags] : [];
        const request = new UpdateReceiptRequest({
          actions: saveTags,
          note: changes.note ?? detail.receipt.note ?? '',
          numberOfStamps: changes.numberOfStamps ?? null,
          receipt: updatedReceipt,
          isReprocess: changes.hasTagChanges,
          isUpdateReceipt: changes.hasDetailChanges
        });

        try {
          await lastValueFrom(this.receiptApi.updateReceiptById(detail.receipt.id, request));
        } catch (error) {
          console.error(`Failed to update receipt for campaign ${campaignId}:`, error);
          throw error;
        }
        return;
      }

      // Prepare tags with the action tag
      let tags = [...(changes.tags || detail.receipt.tags || [])];

      // Remove previous action tags
      tags = tags.filter(item =>
        ![
          RECEIPT_TAGS.REPROCESS_MANUAL_REJECT_TAG,
          RECEIPT_TAGS.REPROCESS_MANUAL_ACCEPT_TAG,
          RECEIPT_TAGS.REPROCESS_CHECK_FRAUD
        ].includes(item)
      );
      tags = tags.filter(item => !item.startsWith(RECEIPT_TAGS.REPROCESS_MANUAL_GIVE_STAMPS_PREFIX_TAG));

      // Add the appropriate action tag
      if (actionType === 'eligible') {
        tags.push(RECEIPT_TAGS.REPROCESS_MANUAL_ACCEPT_TAG);
      } else if (actionType === 'fraud') {
        tags.push(RECEIPT_TAGS.REPROCESS_CHECK_FRAUD);
      } else if (actionType === 'reject') {
        tags.push(RECEIPT_TAGS.REPROCESS_MANUAL_REJECT_TAG);
      }

      const request = new UpdateReceiptRequest({
        actions: tags,
        note: changes.note ?? detail.receipt.note ?? '',
        numberOfStamps: changes.numberOfStamps ?? null,
        receipt: updatedReceipt,
        isReprocess: true,
        isUpdateReceipt: changes.hasDetailChanges
      });

      try {
        await lastValueFrom(this.receiptApi.updateReceiptById(detail.receipt.id, request));
      } catch (error) {
        console.error(`Failed to update receipt for campaign ${campaignId}:`, error);
        throw error;
      }
    });

    try {
      await Promise.all(updatePromises);

      // Reload all receipt data
      await this.loadReceiptData();
      this.receiptUpdated.emit(this.receipt);
      this.setButtonDisable(false);

      // For save action, show success message
      // For action buttons (eligible/fraud/reject), don't show message as the undo countdown is already active
      if (actionType === 'save') {
        this.snackbar.open(
          `Successfully updated ${campaignIds.length} campaign(s)!`,
          null,
          { duration: 3000, horizontalPosition: 'left' }
        );
      }
    } catch (error) {
      this.snackbar.open(
        'Some receipts failed to update. Please check and try again.',
        null,
        { duration: 5000, horizontalPosition: 'left' }
      );
      this.setButtonDisable(false);

      // Clear multi-campaign undo state on error
      if (actionType !== 'save') {
        this.clearActionUndoTimer();
        this.multiCampaignOriginalState.clear();
        this.selectedCampaignIdsForUndo = [];
        this.currentActionType = null;
      }
    }
  }

  removeTagPreviousAction() {
    // Remove from receipt tags
    if (this.receipt.tags) {
      this.receipt.tags = this.receipt.tags.filter(item =>
        ![RECEIPT_TAGS.REPROCESS_MANUAL_REJECT_TAG, RECEIPT_TAGS.REPROCESS_MANUAL_ACCEPT_TAG].includes(item)
      );
      // Remove both ManualGiveStamps and ManualGivePoints tags
      this.receipt.tags = this.receipt.tags.filter(item =>
        !item.startsWith(RECEIPT_TAGS.REPROCESS_MANUAL_GIVE_STAMPS_PREFIX_TAG) &&
        !item.startsWith(RECEIPT_TAGS.REPROCESS_MANUAL_GIVE_POINTS_PREFIX_TAG)
      );
    }

    // Sync component tags array with receipt tags
    this.tags = this.receipt.tags ? [...this.receipt.tags] : [];

    // If using multiple receipts, sync detail object
    if (this.currentReceiptDetail && this.currentReceiptDetail.receipt) {
      this.currentReceiptDetail.receipt.tags = this.receipt.tags;
    }

    this.isGiveStampsChecked = false;
    this.isCheckEligibleChecked = false;
    this.isRejectChecked = false;
  }

  updateTagChecks(isInitialState: boolean) {
    this.isCheckEligibleChecked = this.tags?.includes(RECEIPT_TAGS.REPROCESS_MANUAL_ACCEPT_TAG);
    this.isCheckFraudChecked = this.tags?.includes(RECEIPT_TAGS.REPROCESS_CHECK_FRAUD);
    // Check for both ManualGiveStamps and ManualGivePoints tags
    this.isGiveStampsChecked = this.tags?.some(tag =>
      tag.startsWith(RECEIPT_TAGS.REPROCESS_MANUAL_GIVE_STAMPS_PREFIX_TAG) ||
      tag.startsWith(RECEIPT_TAGS.REPROCESS_MANUAL_GIVE_POINTS_PREFIX_TAG)
    );
    this.isRejectChecked = this.tags?.includes(RECEIPT_TAGS.REPROCESS_MANUAL_REJECT_TAG);

    // Check for ManualGiveStamps or ManualGivePoints tag and extract the number
    const stampTags = this.tags?.filter(x => x.startsWith(RECEIPT_TAGS.REPROCESS_MANUAL_GIVE_STAMPS_PREFIX_TAG)) || [];
    const pointTags = this.tags?.filter(x => x.startsWith(RECEIPT_TAGS.REPROCESS_MANUAL_GIVE_POINTS_PREFIX_TAG)) || [];

    if (stampTags.length > 0) {
      const numberOfStamps = parseInt(stampTags[0].substring(stampTags[0].lastIndexOf('_') + 1));
      this.mainForm.get('numberOfStamps')?.setValue(numberOfStamps);
    } else if (pointTags.length > 0) {
      const numberOfPoints = parseInt(pointTags[0].substring(pointTags[0].lastIndexOf('_') + 1));
      this.mainForm.get('numberOfStamps')?.setValue(numberOfPoints);
    }

    if (this.tags?.includes(RECEIPT_TAGS.REPROCESS_TAG)) {
      this.setButtonDisable(true);
    } else {
      this.setButtonDisable(false);
      if (!isInitialState) {
        this.tagsChangedHandler(true);
      }
    }
  }

  setButtonDisable(disabled: boolean) {
    this.rejectDisabled = disabled;
    this.checkEligibleDisabled = disabled;
    this.giveStampDisabled = disabled;
    this.checkFraudDisabled = disabled;
    if (disabled) {
      this.saveDisabled = true;
    } else {
      this.refreshPushToRepocessingStatus(this.tags);
    }
  }

  fillReprocessingData() {
    this.tags = this.receipt?.tags || [];
    this.mainForm.patchValue({
      note: this.receipt?.note || ''
    });
  }

  formatDate(date: Date | undefined): string {
    if (!date) return 'N/A';
    return format(new Date(date), 'MMM dd, yyyy, hh:mm:ss a');
  }

  getLastEditInfo(): string {
    if (!this.receiptChanges || this.receiptChanges.length === 0) {
      return '';
    }

    const isAutomaticAuthor = (ch: any) => {
      const by = (ch.changedBy || '').toString().trim().toLowerCase();
      return by === 'reprocessing';
    };

    let lastChange: any = this.receiptChanges[0];

    if (isAutomaticAuthor(lastChange)) {
      const nextReal = this.receiptChanges.slice(1).find(ch => !isAutomaticAuthor(ch));
      if (nextReal) {
        lastChange = nextReal;
      }
    }

    const user = lastChange.changedBy || 'Unknown user';

    if (!lastChange.date) {
      return `Last edit: Unknown date by ${user}`;
    }

    let changeDate: Date;
    if (typeof lastChange.date === 'string') {
      const ds = lastChange.date as string;
      if (/[zZ]$|[+-]\d{2}:?\d{2}$/.test(ds)) {
        changeDate = new Date(ds);
      } else {
        changeDate = new Date(ds + 'Z');
      }
    } else {
      changeDate = new Date(lastChange.date);
    }
    const now = new Date();
    const diffInMs = now.getTime() - changeDate.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));

    let timeText: string;

    if (diffInMinutes < 1) {
      timeText = 'just now';
    } else if (diffInMinutes < 60) {
      timeText = 'just a few minutes ago';
    } else if (diffInHours < 24) {
      timeText = `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    } else {
      timeText = format(changeDate, 'MMM dd, yyyy, hh:mm a');
    }

    return `Last edit: ${timeText} by ${user}`;
  }

  getDecision(): string {
    if (!this.receipt) {
      return 'Pending';
    }

    const tags = this.receipt.tags ?? [];
    const fraudDecision = this.receipt.meta?.fraud?.decision;

    const hasManualAccept = tags.includes(RECEIPT_TAGS.REPROCESS_MANUAL_ACCEPT_TAG);
    const hasManualReject = tags.includes(RECEIPT_TAGS.REPROCESS_MANUAL_REJECT_TAG);
    const hasToCheckFraud = tags.includes(RECEIPT_TAGS.REPROCESS_CHECK_FRAUD);
    const hasToManualVerify = tags.includes(RECEIPT_TAGS.MANUAL_VERIFY_TAG);
    const hasReceivedStamps = tags.includes(RECEIPT_TAGS.STAMPS_RECEIVED_TAG);
    const hasReceivedPoints = tags.includes(RECEIPT_TAGS.POINTS_RECEIVED_TAG);

    const hasPunchcardCompleted = tags.some(tag => tag.toLowerCase().includes('punchcardcompleted'));
    const hasLimitationTag = tags.some(tag =>
      tag.toLowerCase().includes('limitation') ||
      tag.toLowerCase().includes('receipt-review')
    );

    if (hasManualAccept || hasReceivedStamps || hasReceivedPoints) {
      return 'Cleared';
    }

    if (hasManualReject || hasToCheckFraud) {
      return 'Fraud';
    }

    if (hasPunchcardCompleted) {
      return 'Ineligible';
    }

    if (hasLimitationTag) {
      return 'Cleared';
    }

    if (hasToManualVerify) {
      return 'Pending';
    }

    if (fraudDecision) {
      return fraudDecision === 'fraud' ? 'Fraud' : 'Cleared';
    }

    return 'Pending';
  }

  getDecisionColor(): string {
    const decision = this.getDecision();
    switch (decision.toLowerCase()) {
      case 'cleared': return '#4caf50';
      case 'fraud': return '#f44336';
      case 'pending': return '#ff9800';
      case 'ineligible': return '#9e9e9e';
      default: return '#ff9800';
    }
  }

  getStage() {
    if (!this.receipt?.tags) return '';

    const tags = this.receipt.tags;
    if (tags.includes(RECEIPT_TAGS.NOT_ENOUGH_STAMPS)) return 'Not enough stamp';
    if (tags.includes(RECEIPT_TAGS.STAMPS_RECEIVED_TAG) || tags.includes(RECEIPT_TAGS.POINTS_RECEIVED_TAG)) return 'Eligible';
    if (tags.includes(RECEIPT_TAGS.NOT_RECOGNIZABLE_TAG)) return 'Not recognizable';
    if (tags.includes(RECEIPT_TAGS.VERIFY_DUPLICATE_TAG)) return 'Veryfi duplication';
    if (tags.includes(RECEIPT_TAGS.SUSPECT_DUPLICATE_TAG)) return 'Suspect duplicated';
    if (tags.includes(RECEIPT_TAGS.SUSPECT_LOCATION_TAG)) return 'Suspect location';
    if (tags.includes(RECEIPT_TAGS.TIME_CONSTRAINT_NOT_VALID)) return 'Time constraint not valid';
    if (tags.includes(RECEIPT_TAGS.NOT_ELIGIBLE_TAG)) return 'Not eligible';
    if (tags.includes(RECEIPT_TAGS.INCOMPLETE_TAG)) return 'Incomplete';
    if (tags.includes(RECEIPT_TAGS.MANUAL_VERIFY_TAG)) return 'Forced manual review';
    return 'unknown';
  }

  shouldShowDuplicateOriginalLink(): boolean {
    const tags = this.currentReceiptDetail?.receipt?.tags ?? this.receipt?.tags ?? [];
    if (!this.hasDuplicateTagValue(tags)) {
      return false;
    }
    // Show "View Original Receipt" only if this specific duplicate receipt hasn't viewed its original yet
    return !this.hasViewedOriginalForCurrentReceipt();
  }

  shouldShowDuplicateOriginalsLink(): boolean {
    const tags = this.currentReceiptDetail?.receipt?.tags ?? this.receipt?.tags ?? [];

    // For duplicate receipts: show "Search Duplicates" ONLY if THIS SPECIFIC receipt has viewed its original
    if (this.hasDuplicateTagValue(tags)) {
      return this.hasViewedOriginalForCurrentReceipt();
    }

    // For original receipts (no duplicate tags): show "Search Duplicates" if ANY duplicate with this
    // invoice number has viewed its original (meaning someone came from a duplicate)
    return this.hasViewedOriginalForInvoiceNumber();
  }

  private hasDuplicateTagValue(tags?: (string | null | undefined)[]): boolean {
    if (!tags || tags.length === 0) {
      return false;
    }

    return tags.some(tag => {
      if (!tag) {
        return false;
      }
      const normalized = tag.trim().toLowerCase();
      return this.duplicateTagValues.has(normalized);
    });
  }

  async openDuplicateOriginalReceipt(): Promise<void> {
    if (this.isLoadingOriginalReceipt) {
      return;
    }

    const invoiceControl = this.mainForm.get('invoiceNumber');
    const invoiceNumber = (invoiceControl?.value || this.invoiceNumber || '').trim();

    if (!invoiceNumber) {
      this.snackbar.open('Invoice number is required to locate the original receipt', null, {
        duration: 3000,
        horizontalPosition: 'left'
      });
      return;
    }

    this.isLoadingOriginalReceipt = true;

    try {
      const response = await lastValueFrom(this.receiptApi.searchReceiptsByText(invoiceNumber, 0, 50));
      const receipts = response.receipts ?? [];

      if (receipts.length === 0) {
        this.snackbar.open('Original receipt not found', null, { duration: 3000, horizontalPosition: 'left' });
        return;
      }

      const normalizedInvoice = invoiceNumber.toLowerCase();

      const candidate = receipts
        .filter(r => (r.invoiceNumber || '').trim().toLowerCase() === normalizedInvoice)
        .filter(r => !this.hasDuplicateTagValue(r.tags))
        .filter(r => r.id !== this.receipt?.id)
        .sort((a, b) => {
          const aTime = a.createDate ? new Date(a.createDate).getTime() : 0;
          const bTime = b.createDate ? new Date(b.createDate).getTime() : 0;
          return aTime - bTime;
        })[0];

      if (!candidate) {
        this.snackbar.open('Original receipt not found', null, { duration: 3000, horizontalPosition: 'left' });
        return;
      }

      // Mark the current receipt ID (the duplicate) and invoice number as having viewed its original
      if (this.receipt?.id) {
        this.markOriginalAsViewed(this.receipt.id, invoiceNumber);
      }

      if (this.parentPanel) {
        this.parentPanel.open(candidate);
        return;
      }

      if (candidate.id) {
        await this.router.navigate(['/receipts', candidate.id]);
      }
    } catch (error) {
      console.error('Error fetching original receipt:', error);
      this.snackbar.open('Failed to load original receipt', null, { duration: 3000, horizontalPosition: 'left' });
    } finally {
      this.isLoadingOriginalReceipt = false;
    }
  }

  hasDuplicates(): boolean {
    return !!this.receipt?.suspectDuplicateGroup;
  }

  getDuplicatesText(): string {
    return this.receipt?.suspectDuplicateGroup || 'Search Duplicates';
  }

  searchDuplicates() {
    // Use invoice number if available, otherwise fall back to suspectDuplicateGroup
    const invoiceNumber = this.receipt?.invoiceNumber;

    if (invoiceNumber) {
      // Search by invoice number to find all related receipts
      const queryParams = {
        searchText: invoiceNumber,
        keepPanelOpen: 'true', // Flag to prevent panel from closing
        v: this.unique_id()
      };

      this.router.navigate(['/receipts'], { queryParams });
    } else if (this.receipt?.suspectDuplicateGroup) {
      // Fallback to original logic if no invoice number
      const queryParams = {
        suspectDuplicateGroup: this.receipt.suspectDuplicateGroup,
        v: this.unique_id()
      };

      this.router.navigate(['/receipts'], { queryParams });
    }
  }

  unique_id() {
    return 'xxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  onImageClick() {
    this.imageClicked.emit();
  }

  openReceiptImage(event?: MouseEvent) {
    event?.stopPropagation();
    const url = this.receipt?.receiptUrl;
    if (!url) {
  this.snackbar.open('Receipt image not available', null, { duration: 3000, horizontalPosition: 'left' });
      return;
    }
    window.open(url, '_blank');
  }

  openImageWithOcr(): void {
    const imageUrl = this.receipt?.receiptUrl;
    const ocrText = this.receipt?.ocrText || this.currentReceiptDetail?.receipt?.ocrText || '';

    if (!imageUrl) {
      this.snackbar.open('Receipt image not available', null, { duration: 3000, horizontalPosition: 'left' });
      return;
    }

    if (!ocrText) {
      this.snackbar.open('No OCR text available for this receipt', null, { duration: 3000, horizontalPosition: 'left' });
      return;
    }

    // Call parent's openImageViewer with OCR text
    if (this.parentPanel) {
      this.parentPanel.openImageViewer(true, ocrText);
    }
  }

  async onSeeCampaign() {
    if (!this.receipt?.campaignId) return;

    // Use existing campaign info if available (from docId mode)
    let campaign = this.campaignInfo;

    // Otherwise fetch it
    if (!campaign) {
      campaign = await lastValueFrom(this.campaignsClient.getCampaign(this.receipt.campaignId));
      if (!campaign) {
  this.snackbar.open('Campaign not found', 'Okay', { duration: 3000, horizontalPosition: 'left' });
        return;
      }
    }

    let url;
    const isMultiCampaign = !campaign.isPunchCardStrategy &&
                           !campaign.isLuckyDrawStrategy &&
                           !campaign.isStaffCampaignStrategy &&
                           !campaign.isReferralCampaignStrategy;

    if (campaign.isStaffCampaignStrategy)
      url = `${environment.baseUrl}/staff-campaign-edit/${campaign.advertiserId}/${campaign.campaignId}`;
    if (campaign.isReferralCampaignStrategy)
      url = `${environment.baseUrl}/referral-campaign-edit/${campaign.advertiserId}/${campaign.campaignId}`;
    if (campaign.isPunchCardStrategy)
      url = `${environment.baseUrl}/punch-card-campaign-edit/${campaign.advertiserId}/${campaign.campaignId}`;
    if (campaign.isLuckyDrawStrategy)
      url = `${environment.baseUrl}/lucky-draw-campaign-edit/${campaign.advertiserId}/${campaign.campaignId}`;
    if (isMultiCampaign)
      url = `${environment.baseUrl}/multi-campaign-edit/${campaign.advertiserId}/${campaign.campaignId}`;

    if (url) {
      window.open(url, '_blank');
    }
  }

  markAllAsTouched(formGroup: any) {
    (Object as any).values(formGroup.controls).forEach((control: any) => {
      control.markAsTouched();
      control.updateValueAndValidity();

      if (control.controls) {
        this.markAllAsTouched(control);
      }
    });
  }

  async submit() {
    this.markAllAsTouched(this.mainForm);
    this.setButtonDisable(true);

    const note = this.mainForm.get('note')?.value || '';
    const numberOfStamps = this.mainForm.get('numberOfStamps')?.value;
    const tags = this.tags;

    if (!this.mainForm.valid) {
      const confirmLabel = this.saveText;
      const confirmMessage = `Line item details is missing. Are you sure you want to ${confirmLabel.toLowerCase()} this receipt?`;
      const confirmed = await lastValueFrom(
        this.dialog.open(ReceiptSavingConfirmationComponent, {
          data: {
            confirmLabel,
            message: confirmMessage
          }
        }).afterClosed()
      );
      if (confirmed) {
        this.SaveData(tags, note, numberOfStamps, true, false, null);
      } else {
        this.setButtonDisable(false);
      }
      return;
    }

    const lineItems = this.mainForm.get('lineItems') as FormArray;
    this.raw.line_items = lineItems?.value;
    this.raw.date = this.receipt?.invoiceDate;
    this.raw.invoice_number = this.receipt?.invoiceNumber;
    this.raw.vendor.name = this.receipt?.vendor?.name;
    this.receipt.rawJson = this.raw && JSON.stringify(this.raw);

    const lineItemData = lineItems.value?.map(
      (lineItem: any) => new LineItem({
        name: lineItem.description,
        amount: lineItem.quantity ?? 0,
        price: lineItem.price ?? 0,
        discount: lineItem.discount ?? 0,
        total: lineItem.total ?? 0
      })
    );
    this.receipt.lineItems = lineItemData;
    this.SaveData(tags, note, numberOfStamps, this.isTagChanged, this.isDetailsChanged, this.receipt);
  }

  async submitSingleReceipt() {
    this.markAllAsTouched(this.mainForm);
    this.setButtonDisable(true);

    const note = this.mainForm.get('note')?.value || '';
    const numberOfStamps = this.mainForm.get('numberOfStamps')?.value;
    const tags = this.tags;

    if (!this.mainForm.valid) {
      const confirmLabel = this.saveText;
      const confirmMessage = `Line item details is missing. Are you sure you want to ${confirmLabel.toLowerCase()} this receipt?`;
      const confirmed = await lastValueFrom(
        this.dialog.open(ReceiptSavingConfirmationComponent, {
          data: {
            confirmLabel,
            message: confirmMessage
          }
        }).afterClosed()
      );
      if (confirmed) {
        this.SaveData(tags, note, numberOfStamps, true, false, null);
      } else {
        this.setButtonDisable(false);
      }
      return;
    }

    const lineItems = this.mainForm.get('lineItems') as FormArray;
    this.raw.line_items = lineItems?.value;
    this.raw.date = this.receipt?.invoiceDate;
    this.raw.invoice_number = this.receipt?.invoiceNumber;
    this.raw.vendor.name = this.receipt?.vendor?.name;
    this.receipt.rawJson = this.raw && JSON.stringify(this.raw);

    const lineItemData = lineItems.value?.map(
      (lineItem: any) => new LineItem({
        name: lineItem.description,
        amount: lineItem.quantity ?? 0,
        price: lineItem.price ?? 0,
        discount: lineItem.discount ?? 0,
        total: lineItem.total ?? 0
      })
    );
    this.receipt.lineItems = lineItemData;
    this.SaveData(tags, note, numberOfStamps, this.isTagChanged, this.isDetailsChanged, this.receipt);
  }

  private SaveData(tags: string[], note: string, numberOfStamps: any,
                   isReprocess: boolean, isUpdateReceipt: boolean, receipt: Receipt | null) {
    const request = new UpdateReceiptRequest({
      actions: tags,
      note: note,
      numberOfStamps: numberOfStamps,
      receipt: receipt,
      isReprocess: isReprocess,
      isUpdateReceipt: isUpdateReceipt
    });

    this.receiptApi
      .updateReceiptById(this.receipt.id, request)
      .pipe(untilDestroyed(this))
      .subscribe(async () => {
        if (isReprocess === false && !this.receipt?.tags?.includes(RECEIPT_TAGS.REPROCESS_TAG)) {
          this.setButtonDisable(false);
        }
        this.isTagChanged = false;
        this.isDetailsChanged = false;
        this.saveText = "Save";
  this.snackbar.open('Update receipt successfully!', null, { duration: 3000, horizontalPosition: 'left' });

        // Reload receipt data
        await this.reloadReceipt();
      });
  }

  async reloadReceipt() {
    this.receipt = await lastValueFrom(this.receiptApi.getReceiptById(this.receipt.id));
    await this.loadReceiptData();
    this.receiptUpdated.emit(this.receipt);
  }

  async getReceiptChanges(): Promise<ReceiptHistoryChange[]> {
    if (!this.receipt?.id) return [];
    return await lastValueFrom(this.receiptApi.getReceiptHistory(this.receipt.id));
  }

  refreshPushToRepocessingStatus(tags: string[] | undefined) {
    if (!tags) return;

    if ((tags.includes(RECEIPT_TAGS.REPROCESS_MANUAL_ACCEPT_TAG) ||
         tags.includes(RECEIPT_TAGS.REPROCESS_CHECK_FRAUD) ||
         tags.some(tag => tag.startsWith(RECEIPT_TAGS.REPROCESS_MANUAL_GIVE_STAMPS_PREFIX_TAG)) ||
         tags.includes(RECEIPT_TAGS.REPROCESS_MANUAL_REJECT_TAG))) {
      this.saveDisabled = false;
    }
    if (tags.includes(RECEIPT_TAGS.REPROCESS_TAG)) {
      this.saveDisabled = true;
    }
    if (this.isDetailsChanged || this.isTagChanged) {
      this.saveDisabled = false;
    }
  }

  tagsChangedHandler($event: boolean) {
    if ($event === true) {
      this.isTagChanged = true;
      this.saveText = 'Save and Reprocess';
      this.refreshPushToRepocessingStatus(this.tags);
      this.saveCampaignChanges(); // Track changes for this campaign
    }
  }

  detailsChangedHandler() {
    this.isDetailsChanged = true;
    this.refreshPushToRepocessingStatus(this.tags);
    this.saveCampaignChanges(); // Track changes for this campaign
  }

  copyJsonToClipboard() {
    const jsonString = JSON.stringify(this.raw, null, 2);
    navigator.clipboard.writeText(jsonString).then(() => {
  this.snackbar.open('JSON copied to clipboard!', null, { duration: 2000, horizontalPosition: 'left' });
    }).catch(err => {
  this.snackbar.open('Failed to copy JSON', null, { duration: 2000, horizontalPosition: 'left' });
      console.error('Failed to copy: ', err);
    });
  }

  // Keyboard shortcuts for campaign tab navigation
  @HostListener('document:keydown.arrowleft', ['$event'])
  handleLeftArrow(event: KeyboardEvent) {
    if (this.shouldIgnoreShortcut(event)) {
      return;
    }

    // Only handle if we have multiple campaign tabs
    if (this.receiptDetails.length > 1) {
      event.preventDefault();
      this.navigateToPreviousTab();
    }
  }

  @HostListener('document:keydown.arrowright', ['$event'])
  handleRightArrow(event: KeyboardEvent) {
    if (this.shouldIgnoreShortcut(event)) {
      return;
    }

    // Only handle if we have multiple campaign tabs
    if (this.receiptDetails.length > 1) {
      event.preventDefault();
      this.navigateToNextTab();
    }
  }

  @HostListener('document:keydown', ['$event'])
  async handleActionShortcuts(event: KeyboardEvent) {
    const key = event.key?.toLowerCase();

    if (key === 'escape' || event.key === 'Esc') {
      if (this.dialog.openDialogs.length > 0) {
        const dialogRef = this.dialog.openDialogs[this.dialog.openDialogs.length - 1];
        dialogRef.close();
        event.preventDefault();
        event.stopImmediatePropagation();
        event.stopPropagation();
        return;
      }

      const handled = this.handleEscapeShortcut();
      if (handled) {
        event.preventDefault();
        event.stopImmediatePropagation();
        event.stopPropagation();
      }
      return;
    }

    if (this.shouldIgnoreShortcut(event)) {
      return;
    }

    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }

    switch (key) {
      case 'f':
        if (!this.checkFraudDisabled) {
          event.preventDefault();
          await this.checkFraud();
        }
        break;
      case 'r':
        if (!this.rejectDisabled) {
          event.preventDefault();
          await this.reject();
        }
        break;
      case 'e':
        if (!this.checkEligibleDisabled) {
          event.preventDefault();
          await this.checkEligible();
        }
        break;
      case 'l':
        if (this.addLineItemViaShortcut()) {
          event.preventDefault();
        }
        break;
      case 'p':
        if (this.focusPointsInput()) {
          event.preventDefault();
        }
        break;
      case 's':
        if (this.focusStampsInput()) {
          event.preventDefault();
        }
        break;
      case 'h':
        if (this.receiptChanges && this.receiptChanges.length > 0) {
          event.preventDefault();
          this.openChangelogDialog();
        }
        break;
      default:
        break;
    }
  }

  private addLineItemViaShortcut(): boolean {
    const addButton = document.querySelector('.line-items-section .section-header button[mat-icon-button]');
    if (!addButton) {
      return false;
    }

    this.addLineItem();
  this.focusElement(() => document.querySelector('.line-items-section .line-items-list .line-item-card:last-child input[formcontrolname="description"]') as HTMLInputElement | null);
    return true;
  }

  private focusPointsInput(): boolean {
    const rowExists = this.findCampaignInputRow('Points Received');
    if (!rowExists) {
      return false;
    }

    this.focusElement(() => this.findCampaignInputRow('Points Received')?.querySelector('input') as HTMLInputElement | null);
    return true;
  }

  private focusStampsInput(): boolean {
    const rowExists = this.findCampaignInputRow('Stamps Received');
    if (!rowExists) {
      return false;
    }

    this.focusElement(() => this.findCampaignInputRow('Stamps Received')?.querySelector('input') as HTMLInputElement | null);
    return true;
  }

  private findCampaignInputRow(labelText: string): HTMLElement | null {
    const rows = Array.from(document.querySelectorAll('.campaign-info-row.editable-row')) as HTMLElement[];
    const matchText = labelText.toLowerCase();
    return rows.find(row => {
      const label = row.querySelector('.campaign-label');
      return label?.textContent?.toLowerCase().includes(matchText);
    }) ?? null;
  }

  private focusElement(getter: () => HTMLElement | null): void {
    setTimeout(() => {
      requestAnimationFrame(() => {
        const element = getter();
        if (!element) {
          return;
        }

        element.focus();
        if (element instanceof HTMLInputElement && element.type !== 'number') {
          element.select();
        } else if (element instanceof HTMLTextAreaElement) {
          element.select();
        }
      });
    }, 0);
  }

  private handleEscapeShortcut(): boolean {
    if (this.parentPanel?.showImageViewer) {
      this.parentPanel.closeImageViewer();
      return true;
    }

    const drawer = this.parentPanel?.drawer?.();
    if (drawer?.opened) {
      drawer.close();
      return true;
    }
    return false;
  }

  private shouldIgnoreShortcut(event: KeyboardEvent): boolean {
    const target = event.target as HTMLElement | null;
    if (!target) {
      return false;
    }

    const tagName = target.tagName ? target.tagName.toLowerCase() : '';
    return tagName === 'input' || tagName === 'textarea' || target.isContentEditable;
  }

  private getManualGiveStampsValue(tags: string[] | undefined): number | null {
    if (!tags) return null;
    const tag = tags.find(t => t.startsWith(RECEIPT_TAGS.REPROCESS_MANUAL_GIVE_STAMPS_PREFIX_TAG));
    if (!tag) return null;
    const parts = tag.split('_');
    if (parts.length > 1) {
      const value = parseInt(parts[1], 10);
      return isNaN(value) ? null : value;
    }
    return null;
  }

  private navigateToPreviousTab() {
    if (this.selectedTabIndex > 0) {
      this.selectedTabIndex--;
      this.onTabChange(this.selectedTabIndex);
    }
  }

  private navigateToNextTab() {
    if (this.selectedTabIndex < this.receiptDetails.length - 1) {
      this.selectedTabIndex++;
      this.onTabChange(this.selectedTabIndex);
    }
  }

  ngOnDestroy(): void {
    // Execute any pending saves before destroying component
    this.executePendingSaves();

    // Clean up all undo timers
    this.clearPointsUndoTimer();
    this.clearStampsUndoTimer();
    this.clearActionUndoTimer();

    // Clean up multi-campaign undo state
    this.multiCampaignOriginalState.clear();
    this.selectedCampaignIdsForUndo = [];
  }
}
